import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import Chart from 'react-apexcharts';
import { dashboardData, chartColors } from '../data/dashboardData';
import { dashboardAPI } from '../services/apiClient';
import { firstPlausibleDate, isPlausibleDate, sastBucket } from '../utils/sastDates';
import { appendDateParams } from '../utils/dateParams';
import ValidatorAnalyticsTabs from './ValidatorAnalyticsTabs';

// (Removed unused MemoizedChart)

// Error boundary to catch ApexCharts runtime errors
class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch() {}
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ height: this.props.height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
          <span style={{ fontSize: '12px' }}>Unable to render chart</span>
        </div>
      );
    }
    return this.props.children;
  }
}

// Safer Chart wrapper to avoid ApexCharts xyRatios null errors when mounting
const SafeApexChart = memo(({ options, series, type, height, idPrefix = 'chart' }) => {
  const containerRef = React.useRef(null);
  const [isReady, setIsReady] = useState(false);
  const uniqueId = useMemo(() => `${idPrefix}-${Math.random().toString(36).slice(2)}`, [idPrefix]);

  // Defer initial render to next tick to allow layout
  useEffect(() => {
    const t = setTimeout(() => setIsReady(true), 0);
    return () => clearTimeout(t);
  }, []);

  // Ensure parent has size before rendering chart
  const [hasSize, setHasSize] = useState(true);
  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        setHasSize(cr.width > 0 && cr.height > 0);
      }
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  const mergedOptions = useMemo(() => ({
    ...options,
    chart: {
      ...(options?.chart || {}),
      id: options?.chart?.id || uniqueId,
      animations: { enabled: false, ...(options?.chart?.animations || {}) },
      redrawOnParentResize: true,
      redrawOnWindowResize: true
    }
  }), [options, uniqueId]);

  const categories = Array.isArray(mergedOptions?.xaxis?.categories) ? mergedOptions.xaxis.categories : [];
  const sanitizedSeries = useMemo(() => {
    if (!Array.isArray(series)) return [];
    return series.map(s => ({
      ...s,
      data: Array.isArray(s.data) ? s.data.filter(v => Number.isFinite(Number(v))) : []
    }));
  }, [series]);

  const seriesPointCount = sanitizedSeries.reduce((acc, s) => acc + (Array.isArray(s.data) ? s.data.length : 0), 0);
  const validSeries = sanitizedSeries.length > 0 && seriesPointCount > 0 && categories.length > 0;

  if (!isReady) {
    return <div ref={containerRef} style={{ height }} />;
  }

  if (!hasSize || !validSeries) {
    return (
      <div ref={containerRef} style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#666' }}>
        <span style={{ fontSize: '12px' }}>Preparing chart…</span>
      </div>
    );
  }

  return (
    <div ref={containerRef}>
      <ChartErrorBoundary height={height}>
        <Chart key={`${uniqueId}-${categories.length}-${seriesPointCount}`} options={mergedOptions} series={sanitizedSeries} type={type} height={height} />
      </ChartErrorBoundary>
    </div>
  );
});

// Daily Trends Tab Component - Rebuilt from scratch
const DailyTrendsTab = memo(({ analyticsData, analyticsLoading, selectedModerator }) => {
  // Helper function to safely format dates
  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'Invalid Date';
    }
  };

  // Helper function to safely get numeric value
  const safeNumber = (value, fallback = 0) => {
    const num = Number(value);
    return isNaN(num) ? fallback : num;
  };

  // (Daily validations chart moved to Workload tab)

  const dailyAccuracyChart = useMemo(() => {
    if (!analyticsData?.dailyTrends?.length) return null;
    
    const validTrends = analyticsData.dailyTrends.filter(d => d && d.date);
    if (!validTrends.length) return null;

    return {
      options: {
        chart: { 
          type: 'line', 
          height: 350, 
          background: '#FFFFFF',
          toolbar: { show: true }
        },
        xaxis: { 
          categories: validTrends.map(d => formatDate(d.date)),
          title: { text: 'Date', style: { fontSize: '14px', fontWeight: '600' } },
          labels: { 
            rotate: -45,
            style: { fontSize: '12px' }
          }
        },
        yaxis: { 
          title: { text: 'Accuracy (%)', style: { fontSize: '14px', fontWeight: '600' } },
          labels: { style: { fontSize: '12px' } },
          min: 0,
          max: 100
        },
        colors: ['#28a745'],
        title: { 
          text: 'Daily Accuracy Trends',
          align: 'center',
          style: { fontSize: '16px', fontWeight: '600' }
        },
        stroke: { curve: 'smooth', width: 3 },
        markers: { size: 5, hover: { size: 7 } },
        grid: { borderColor: '#f1f1f1' },
        tooltip: {
          y: {
            formatter: (value) => `${value.toFixed(1)}%`
          }
        }
      },
      series: [{
        name: 'Accuracy (%)',
        data: validTrends.map(d => safeNumber(d.accuracy))
      }]
    };
  }, [analyticsData?.dailyTrends]);

  // (Rolling accuracy moved to Quality tab)

  // (Weekly activity moved to Workload tab)

  // Calculate summary statistics
  const summaryStats = useMemo(() => {
    if (!analyticsData?.dailyTrends?.length) {
      return {
        totalValidations: 0,
        avgAccuracy: 0,
        bestDay: null,
        trend: 'neutral'
      };
    }

    const validTrends = analyticsData.dailyTrends.filter(d => d && d.date);
    const totalValidations = validTrends.reduce((sum, d) => sum + safeNumber(d.slipsValidated), 0);
    const avgAccuracy = validTrends.length > 0 
      ? validTrends.reduce((sum, d) => sum + safeNumber(d.accuracy), 0) / validTrends.length 
      : 0;
    
    const bestDay = validTrends.reduce((best, current) => {
      return safeNumber(current.accuracy) > safeNumber(best?.accuracy) ? current : best;
    }, validTrends[0]);

    // Calculate trend (comparing first half vs second half)
    const midPoint = Math.floor(validTrends.length / 2);
    const firstHalf = validTrends.slice(0, midPoint);
    const secondHalf = validTrends.slice(midPoint);
    
    const firstHalfAvg = firstHalf.length > 0 
      ? firstHalf.reduce((sum, d) => sum + safeNumber(d.accuracy), 0) / firstHalf.length 
      : 0;
    const secondHalfAvg = secondHalf.length > 0 
      ? secondHalf.reduce((sum, d) => sum + safeNumber(d.accuracy), 0) / secondHalf.length 
      : 0;
    
    let trend = 'neutral';
    if (secondHalfAvg > firstHalfAvg + 2) trend = 'improving';
    else if (firstHalfAvg > secondHalfAvg + 2) trend = 'declining';

    return {
      totalValidations,
      avgAccuracy,
      bestDay,
      trend
    };
  }, [analyticsData?.dailyTrends]);

  if (analyticsLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
        <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '15px' }}></i>
        <p>Loading daily trends analysis...</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '30px' }}>
        <h5 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '20px', fontWeight: '600' }}>
          Daily Performance Trends
        </h5>
        <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
          Detailed analysis of daily validation activity and accuracy patterns for {selectedModerator?.name || 'Selected Validator'}
        </p>
      </div>

      {/* Summary Statistics */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
        gap: '20px', 
        marginBottom: '30px' 
      }}>
        <div style={{ 
          backgroundColor: '#ffffff', 
          borderRadius: '12px', 
          padding: '20px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)', 
          border: '1px solid #e9ecef',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#007bff', marginBottom: '8px' }}>
            {summaryStats.totalValidations.toLocaleString()}
          </div>
          <div style={{ color: '#666', fontSize: '14px' }}>Total Validations</div>
        </div>
        
        <div style={{ 
          backgroundColor: '#ffffff', 
          borderRadius: '12px', 
          padding: '20px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)', 
          border: '1px solid #e9ecef',
          textAlign: 'center'
        }}>
          <div style={{ 
            fontSize: '28px', 
            fontWeight: 'bold', 
            color: summaryStats.avgAccuracy >= 95 ? '#28a745' : summaryStats.avgAccuracy >= 90 ? '#fd7e14' : '#dc3545',
            marginBottom: '8px' 
          }}>
            {summaryStats.avgAccuracy.toFixed(1)}%
          </div>
          <div style={{ color: '#666', fontSize: '14px' }}>Average Accuracy</div>
        </div>
        
        <div style={{ 
          backgroundColor: '#ffffff', 
          borderRadius: '12px', 
          padding: '20px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)', 
          border: '1px solid #e9ecef',
          textAlign: 'center'
        }}>
          <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#28a745', marginBottom: '8px' }}>
            {summaryStats.bestDay ? formatDate(summaryStats.bestDay.date) : 'N/A'}
          </div>
          <div style={{ color: '#666', fontSize: '14px' }}>Best Performance Day</div>
          {summaryStats.bestDay && (
            <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
              {summaryStats.bestDay.accuracy.toFixed(1)}% accuracy
            </div>
          )}
        </div>
        
        <div style={{ 
          backgroundColor: '#ffffff', 
          borderRadius: '12px', 
          padding: '20px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)', 
          border: '1px solid #e9ecef',
          textAlign: 'center'
        }}>
          <div style={{ 
            fontSize: '28px', 
            fontWeight: 'bold', 
            color: summaryStats.trend === 'improving' ? '#28a745' : summaryStats.trend === 'declining' ? '#dc3545' : '#6c757d',
            marginBottom: '8px' 
          }}>
            {summaryStats.trend === 'improving' ? '📈' : summaryStats.trend === 'declining' ? '📉' : '➡️'}
          </div>
          <div style={{ color: '#666', fontSize: '14px' }}>Performance Trend</div>
          <div style={{ fontSize: '12px', color: '#999', marginTop: '4px', textTransform: 'capitalize' }}>
            {summaryStats.trend}
          </div>
        </div>
      </div>

      

      {/* Daily Accuracy Chart */}
      <div style={{ 
        backgroundColor: '#ffffff', 
        borderRadius: '12px', 
        padding: '25px', 
        marginBottom: '25px', 
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)', 
        border: '1px solid #e9ecef' 
      }}>
        <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
          <i className="fas fa-bullseye" style={{ marginRight: '8px', color: '#28a745' }}></i>
          Daily Accuracy Trends
        </h6>
        {dailyAccuracyChart ? (
          <div className="chart-container">
            <SafeApexChart
              idPrefix="daily-accuracy"
              options={dailyAccuracyChart.options}
              series={dailyAccuracyChart.series}
              type="line"
              height={350}
            />
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
            <i className="fas fa-bullseye" style={{ fontSize: '48px', marginBottom: '15px', color: '#e9ecef' }}></i>
            <p style={{ margin: '0', fontSize: '16px' }}>No daily accuracy data available</p>
            <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#999' }}>
              Accuracy trends will be displayed once sufficient data is available
            </p>
          </div>
        )}
      </div>

      

      {/* Weekly Activity Chart */}
      

      {/* Performance Insights */}
      {analyticsData?.dailyTrends?.length > 0 && (
        <div style={{ 
          backgroundColor: '#ffffff', 
          borderRadius: '12px', 
          padding: '25px', 
          marginBottom: '25px', 
          boxShadow: '0 4px 6px rgba(0,0,0,0.1)', 
          border: '1px solid #e9ecef' 
        }}>
          <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
            <i className="fas fa-lightbulb" style={{ marginRight: '8px', color: '#ffc107' }}></i>
            Performance Insights
          </h6>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px' }}>
            {summaryStats.trend === 'improving' && (
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#d4edda', 
                border: '1px solid #c3e6cb', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '24px', marginRight: '15px' }}>🎉</span>
                <div>
                  <div style={{ fontWeight: '600', color: '#155724', marginBottom: '5px' }}>
                    Great Progress!
                  </div>
                  <div style={{ fontSize: '14px', color: '#155724' }}>
                    Your accuracy has been improving over time. Keep up the excellent work!
                  </div>
                </div>
              </div>
            )}
            
            {summaryStats.trend === 'declining' && (
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#f8d7da', 
                border: '1px solid #f5c6cb', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '24px', marginRight: '15px' }}>⚠️</span>
                <div>
                  <div style={{ fontWeight: '600', color: '#721c24', marginBottom: '5px' }}>
                    Attention Needed
                  </div>
                  <div style={{ fontSize: '14px', color: '#721c24' }}>
                    Your accuracy has been declining recently. Consider reviewing common error patterns.
                  </div>
                </div>
              </div>
            )}
            
            {summaryStats.avgAccuracy >= 95 && (
              <div style={{ 
                padding: '15px', 
                backgroundColor: '#d1ecf1', 
                border: '1px solid #bee5eb', 
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '24px', marginRight: '15px' }}>🏆</span>
                <div>
                  <div style={{ fontWeight: '600', color: '#0c5460', marginBottom: '5px' }}>
                    Top Performer
                  </div>
                  <div style={{ fontSize: '14px', color: '#0c5460' }}>
                    Excellent accuracy rate of {summaryStats.avgAccuracy.toFixed(1)}%! You're exceeding expectations.
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

const ModeratorPerformance = ({ dateRange, onRegisterDrillDownTrigger }) => {
  // State for all moderator data (from API - no server-side pagination)
  const [allModerators, setAllModerators] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  
  // State for client-side pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // State for sorting
  const [sortBy, setSortBy] = useState('accuracy');
  const [sortDirection, setSortDirection] = useState('desc');

  // State for drill down dialog
  const [drillDownOpen, setDrillDownOpen] = useState(false);
  const [selectedModerator, setSelectedModerator] = useState(null);

  // State for coupon redemptions in drill down
  const [couponRedemptions, setCouponRedemptions] = useState([]);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState(false);
  const [couponPagination, setCouponPagination] = useState({
    pageNumber: 1,
    pageSize: 10,
    total: 0,
    pageCount: 0
  });
  const [couponCurrentPage, setCouponCurrentPage] = useState(1);
  const [couponPageSize, setCouponPageSize] = useState(10);
  const [revalidatingCouponId, setRevalidatingCouponId] = useState(null);

  // State for status filtering in drill down
  const [statusFilter, setStatusFilter] = useState(null);
  // Derived reason filter (e.g., below min spend, exceeds max discount, no HILLS)
  const [derivedFilter, setDerivedFilter] = useState(null);

  // Retailer accuracy map for ribbons/badges
  const [retailerAccuracyMap, setRetailerAccuracyMap] = useState({});

  // Similar slips modal state (based on current dataset)
  const [similarOpen, setSimilarOpen] = useState(false);
  const [similarContext, setSimilarContext] = useState({ retailer: null, campaign: null });
  const [similarItems, setSimilarItems] = useState([]);

  // State for basket details popup
  const [basketDetailsOpen, setBasketDetailsOpen] = useState(false);
  const [selectedBasket, setSelectedBasket] = useState(null);

  // State for analytics tabs
  const [activeTab, setActiveTab] = useState('overview');
  const [analyticsData, setAnalyticsData] = useState({
    dailyTrends: [],
    weeklyTrends: [],
    errorPatterns: [],
    workloadData: { hourlyActivity: [], dayOfWeekActivity: [] },
    qualityInsights: [],
    coachingData: [],
    peerComparison: [],
    retailerPerformance: []
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(false);


  // State for invoice preview
  const [invoicePreviewOpen, setInvoicePreviewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  
  // State for image controls
  const [imageRotation, setImageRotation] = useState(0);
  const [imageZoom, setImageZoom] = useState(1);
  const [imagePanX, setImagePanX] = useState(0);
  const [imagePanY, setImagePanY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Stable deep link for Share Invite (recomputes only when moderator changes)
  const shareInviteLink = useMemo(() => {
    const validatorName = selectedModerator?.name || '';
    if (!validatorName) return '';
    const baseUrl = window.location.origin;
    const uniqueToken = btoa(`${validatorName}|${Date.now()}`);
    return `${baseUrl}/?validator=${encodeURIComponent(validatorName)}&token=${encodeURIComponent(uniqueToken)}`;
  }, [selectedModerator?.name]);

  // Share Invite form component (inline to avoid separate file)
  const ShareInviteForm = ({ moderator, deepLink }) => {
    const [mobile, setMobile] = useState('');
    const [message, setMessage] = useState(() => {
      const name = moderator?.name || 'there';
      return `Hi ${name},\n\nYou’ve been granted access to your validator performance dashboard. Click the secure link below to view your drilldown:\n\n${deepLink}\n\nPlease keep this link confidential. If you have any issues accessing, reply here.\n\nThanks!`;
    });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);

    const formatWhatsAppUrl = (phone, text) => {
      const digits = (phone || '').replace(/\D/g, '');
      const international = digits.startsWith('0') ? `27${digits.slice(1)}` : digits;
      const encoded = encodeURIComponent(text);
      return `https://wa.me/${international}?text=${encoded}`;
    };

    const handleSendWhatsApp = () => {
      if (!mobile) return;
      setSending(true);
      try {
        const url = formatWhatsAppUrl(mobile, message);
        window.open(url, '_blank');
        setSent(true);
      } finally {
        setSending(false);
      }
    };

    return (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '16px' }}>
          {/* Unique Link hidden as requested */}
          <div>
            <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px' }}>Mobile Number (WhatsApp)</label>
            <input
              type="tel"
              placeholder="e.g. 0821234567 or 27821234567"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              style={{ width: '100%', padding: '10px', border: '1px solid #ced4da', borderRadius: '6px' }}
            />
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px' }}>Message</label>
            <textarea
              rows="10"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              style={{ 
                width: '100%', 
                padding: '10px', 
                border: '1px solid #ced4da', 
                borderRadius: '6px', 
                fontFamily: 'inherit',
                boxSizing: 'border-box',
                minHeight: '220px',
                resize: 'vertical',
                display: 'block'
              }}
            />
          </div>
        </div>
        <div style={{ marginTop: '16px', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <button
            onClick={handleSendWhatsApp}
            disabled={!mobile || sending}
            className="drill-btn"
            title="Send WhatsApp message"
          >
            <i className="fab fa-whatsapp" style={{ marginRight: '6px' }}></i>
            {sending ? 'Sending…' : 'Send via WhatsApp'}
          </button>
          {sent && <span style={{ color: '#28a745', fontSize: '12px' }}>Opened WhatsApp with your message</span>}
        </div>
      </div>
    );
  };

  // State for validator accuracy from API (same source as Overview)
  const [validatorAccuracy, setValidatorAccuracy] = useState(null);
  const [validatorAccuracyLoading, setValidatorAccuracyLoading] = useState(false);
  const [validatorAccuracyError, setValidatorAccuracyError] = useState(false);
  
  // Helper function to calculate coupon status (same logic as drill-down)
  const calculateCouponStatus = (item) => {
    let calculatedDiscount = item.calculatedDiscount;
    const discountApplied = item.discountApplied || 0;
    const detectedDiscount = item.detectedDiscount || 0;
    
    // If calculatedDiscount is missing or null, use the coupon amount as the expected value
    if (calculatedDiscount === null || calculatedDiscount === undefined) {
      calculatedDiscount = item.amount || 0;
    }

    // Treat null/missing values as 0
    const discountClaimed = discountApplied || 0;
    let estimatedDue = calculatedDiscount || 0;

    // VAT correction heuristic: if AI estimate excl. VAT times 1.15 is within R10 of claimed,
    // assume data typo and align AI estimate to claimed for UI purposes
    if (estimatedDue > 0 && discountClaimed > 0) {
      const vatAdjustedEstimate = estimatedDue * 1.15;
      if (Math.abs(vatAdjustedEstimate - discountClaimed) <= 10.0) {
        calculatedDiscount = discountApplied;
        estimatedDue = calculatedDiscount;
      }
    }

    // Calculate status based on business logic (5 rand tolerance)
    let status = 'Correct';
    
    const actuallyGiven = detectedDiscount || 0;
    
    // Check for invalid transactions first
    if (!calculatedDiscount && discountApplied === 0) {
      status = 'Invalid';
    } else {
      // Primary check: Claimed vs Actually Given (5 rand tolerance)
      const claimedVsGivenDiff = Math.abs(discountClaimed - actuallyGiven);
      if (claimedVsGivenDiff > 5.00) {
        if (discountClaimed > actuallyGiven) {
          status = 'Under Paid';  // Vet claimed more than actually given - customer underpaid
        } else {
          status = 'Over Paid';   // Vet claimed less than actually given - customer overpaid
        }
      } else {
        // Secondary check: AI Estimate vs Claimed (5 rand tolerance)
        const estimateVsClaimedDiff = Math.abs(estimatedDue - discountClaimed);
        if (estimateVsClaimedDiff > 5.00) {
          if (estimatedDue > discountClaimed) {
            status = 'Discount Calc Too Low';   // AI thinks should be higher
          } else {
            status = 'Discount Calc Too High';  // AI thinks should be lower
          }
        } else {
          status = 'Correct';  // Everything matches (green tick)
        }
      }
    }
    
    return status;
  };

  // Fetch all moderator performance data using same calculation logic as drill-down
  const fetchModerators = useCallback(async () => {
    setLoading(true);
    setError(false);
    
    try {
      // First, get the list of moderators
      const response = await dashboardAPI.getModeratorValidationAccuracy(dateRange);
      const result = response.data;
      const moderatorList = result.data || result || [];
      
      // Fetch coupon redemptions for each moderator and calculate stats using same logic as drill-down
      const moderatorDataPromises = moderatorList.map(async (item) => {
        const moderatorName = item.moderatorName || item.name || 'Unknown';
        
        try {
          // Fetch all coupon redemptions for this moderator
          const couponResponse = await dashboardAPI.getModeratorCouponRedemptions(
            moderatorName,
            0, // skip
            10000, // take - large number to get all
            'redeemed_at',
            'desc',
            '',
            false,
            dateRange
          );
          
          const coupons = couponResponse.data?.items || [];
          
          // Calculate stats using same logic as drill-down
          const statusCounts = coupons.reduce((acc, coupon) => {
            const status = calculateCouponStatus(coupon);
            acc[status] = (acc[status] || 0) + 1;
            return acc;
          }, {});
          
          const correct = statusCounts['Correct'] || 0;
          const overPaid = statusCounts['Over Paid'] || 0;
          const underPaid = statusCounts['Under Paid'] || 0;
          const reviewed = coupons.length;
          const inaccurate = overPaid + underPaid;
          const accuracy = reviewed > 0 ? (correct / reviewed) * 100 : 0;
          
          return {
            name: moderatorName,
            reviewed: reviewed,
            correct: correct,
            accuracy: accuracy,
            inaccurate: inaccurate,
            overPaid: overPaid,
            underPaid: underPaid,
            percentOfTotal: item.percentOfTotal || 0
          };
        } catch (couponError) {
          console.error(`Error fetching coupons for ${moderatorName}:`, couponError);
          // Fallback to API aggregated data if coupon fetch fails
          const reviewed = item.totalCount || item.reviewed || 0;
          const overPaid = item.overPaidCount || 0;
          const underPaid = item.underPaidCount || 0;
          const inaccurate = overPaid + underPaid;
          const correct = reviewed - inaccurate;
          const accuracy = reviewed > 0 ? (correct / reviewed) * 100 : 0;
          
          return {
            name: moderatorName,
            reviewed: reviewed,
            correct: correct,
            accuracy: accuracy,
            inaccurate: inaccurate,
            overPaid: overPaid,
            underPaid: underPaid,
            percentOfTotal: item.percentOfTotal || 0
          };
        }
      });
      
      const moderatorData = await Promise.all(moderatorDataPromises);
      
      setAllModerators(moderatorData);
      setError(false);
    } catch (error) {
      console.error('Error fetching validator performance:', error);
      // Fallback to static data if API fails - ensure all properties are present
      const fallbackData = (dashboardData.moderators || []).map(item => ({
        name: item.name || 'Unknown',
        reviewed: item.reviewed || 0,
        correct: item.correct || 0,
        accuracy: item.accuracy || 0,
        inaccurate: item.inaccurate || 0,
        overPaid: item.overPaid || 0,
        underPaid: item.underPaid || 0,
        percentOfTotal: item.percentOfTotal || 0
      }));
      setAllModerators(fallbackData);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Fetch retailer accuracy list to annotate slips with store quality ribbon
  const fetchRetailerAccuracies = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      // Required pagination/sort defaults for this endpoint
      params.append('Skip', '0');
      params.append('Take', '100');
      params.append('SortBy', 'accuracy');
      params.append('SortDirection', 'desc');
      appendDateParams(params, dateRange);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const resp = await dashboardAPI.getTopRetailersByAccuracy(queryString);
      const items = resp.data?.items || resp.data || [];
      const map = {};
      items.forEach(r => {
        const name = r.retailer || r.name;
        const acc = r.accuracy ?? r.validationAccuracy ?? r.percentAccuracy ?? 0;
        if (name) map[name] = acc;
      });
      setRetailerAccuracyMap(map);
    } catch (e) {
      // Non-blocking
      setRetailerAccuracyMap({});
    }
  }, [dateRange]);

  // Stable key for date range to avoid function identity churn in effects
  const dateRangeKey = `${dateRange?.startDate || ''}|${dateRange?.endDate || ''}|${dateRange?.dateFilterType || 'Redemption'}`;

  // Handle sorting
  const handleSort = (column) => {
    if (sortBy === column) {
      // If clicking the same column, toggle direction
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // If clicking a different column, set new column and default to desc
      setSortBy(column);
      setSortDirection('desc');
    }
    // Reset to first page when sorting changes
    setCurrentPage(1);
  };

  // Get sort indicator for table headers
  const getSortIndicator = (column) => {
    if (sortBy !== column) {
      return ' ↕️'; // Both arrows when not sorted by this column
    }
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  // Sort data before pagination
  const sortedModerators = [...allModerators].sort((a, b) => {
    let aValue, bValue;
    
    switch (sortBy) {
      case 'name':
        aValue = a.name.toLowerCase();
        bValue = b.name.toLowerCase();
        break;
      case 'reviewed':
        aValue = a.reviewed;
        bValue = b.reviewed;
        break;
      case 'correct':
        aValue = a.correct;
        bValue = b.correct;
        break;
      case 'accuracy':
        aValue = a.accuracy;
        bValue = b.accuracy;
        break;
      case 'inaccurate':
        aValue = a.inaccurate;
        bValue = b.inaccurate;
        break;
      case 'overPaid':
        aValue = a.overPaid;
        bValue = b.overPaid;
        break;
      case 'underPaid':
        aValue = a.underPaid;
        bValue = b.underPaid;
        break;
      default:
        aValue = a.accuracy;
        bValue = b.accuracy;
    }

    // Handle string vs number comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      const comparison = aValue.localeCompare(bValue);
      return sortDirection === 'asc' ? comparison : -comparison;
    } else {
      const comparison = aValue - bValue;
      return sortDirection === 'asc' ? comparison : -comparison;
    }
  });

  // Client-side pagination calculations (now using sorted data)
  const totalModerators = sortedModerators.length;
  const totalPages = Math.ceil(totalModerators / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const moderators = sortedModerators.slice(startIndex, endIndex);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  // Fetch validator accuracy using same API as Overview component
  const fetchValidatorAccuracy = useCallback(async () => {
    setValidatorAccuracyLoading(true);
    setValidatorAccuracyError(false);
    
    try {
      const response = await dashboardAPI.getValidatorAccuracy(dateRange);
      const result = response.data;
      
      // Use same logic as Overview component
      let accuracy = 0;
      if (result.accuracy !== undefined) {
        accuracy = result.accuracy;
      } else if (result.count !== undefined) {
        accuracy = result.count;
      } else if (result.percentage !== undefined) {
        accuracy = result.percentage;
      } else if (result.value !== undefined) {
        accuracy = result.value;
      } else if (typeof result === 'number') {
        accuracy = result;
      } else {
        console.warn('Unknown validator accuracy response structure:', result);
        accuracy = 0;
      }
      
      setValidatorAccuracy(accuracy);
      setValidatorAccuracyError(false);
    } catch (error) {
      console.error('Error fetching validator accuracy:', error);
      setValidatorAccuracyError(true);
      // Fallback to calculated average
      if (allModerators.length > 0) {
        const calculatedAverage = allModerators.reduce((sum, m) => sum + m.accuracy, 0) / allModerators.length;
        setValidatorAccuracy(calculatedAverage);
      } else {
        setValidatorAccuracy(0);
      }
    } finally {
      setValidatorAccuracyLoading(false);
    }
  }, [dateRangeKey, allModerators]);

  // Fetch data when component mounts or dateRange changes (not pagination)
  useEffect(() => {
    fetchModerators();
    fetchRetailerAccuracies();
    setCurrentPage(1); // Reset to first page when date range changes
  }, [dateRangeKey]);

  // Fetch validator accuracy after moderators are loaded
  useEffect(() => {
    if (allModerators.length > 0) {
      fetchValidatorAccuracy();
    }
  }, [allModerators.length, dateRangeKey, fetchValidatorAccuracy]);

  // Register drill-down trigger with parent component (will be set up after handleDrillDown is defined)

  // Function to close drill down dialog
  const handleCloseDrillDown = () => {
    setDrillDownOpen(false);
    setSelectedModerator(null);
    // Reset coupon data
    setCouponRedemptions([]);
    setCouponCurrentPage(1);
    setCouponPageSize(10);
    // Reset status filter
    setStatusFilter(null);
    // Reset basket details
    setBasketDetailsOpen(false);
    setSelectedBasket(null);
    // Reset invoice preview
    setInvoicePreviewOpen(false);
    setSelectedInvoice(null);
  };

  // Handle status filter selection
  const handleStatusFilter = (status) => {
    const newFilter = statusFilter === status ? null : status;
    setStatusFilter(newFilter);
    
    // Reset to first page when filtering
    setCouponCurrentPage(1);
  };

  // Handle derived reason filter selection
  const handleDerivedFilter = (reasonKey) => {
    const newFilter = derivedFilter === reasonKey ? null : reasonKey;
    setDerivedFilter(newFilter);
    setCouponCurrentPage(1);
  };

  // Filter coupon redemptions based on status (moved above handlers for accessibility)
  const byStatus = statusFilter 
    ? couponRedemptions.filter(coupon => coupon.status === statusFilter)
    : couponRedemptions;

  const allFilteredCouponRedemptions = derivedFilter
    ? byStatus.filter(c => {
        if (derivedFilter === 'belowMinSpend') return c.flags?.belowMinSpend;
        if (derivedFilter === 'exceedsMaxDiscount') return c.flags?.exceedsMaxDiscount;
        if (derivedFilter === 'noHillsItem') return c.flags?.noHillsItem;
        return true;
      })
    : byStatus;

  // Apply client-side pagination when status filter is active (since we fetched all data)
  // Use server-side pagination when no filter (default behavior)
  const filteredCouponRedemptions = statusFilter 
    ? (() => {
        const startIndex = (couponCurrentPage - 1) * couponPageSize;
        const endIndex = startIndex + couponPageSize;
        return allFilteredCouponRedemptions.slice(startIndex, endIndex);
      })()
    : allFilteredCouponRedemptions;

  // Update pagination info - now always using complete dataset
  const effectivePagination = statusFilter 
    ? {
        // When filtering: use filtered data count
        ...couponPagination,
        total: allFilteredCouponRedemptions.length,
        pageCount: Math.ceil(allFilteredCouponRedemptions.length / couponPageSize)
      }
    : {
        // No filter: use complete coupon data count (we always fetch all)
        ...couponPagination,
        total: couponRedemptions.length,
        pageCount: Math.ceil(couponRedemptions.length / couponPageSize)
      };

  // Fetch coupon redemptions for selected moderator
  const fetchCouponRedemptions = useCallback(async (moderatorName, { silent = false } = {}) => {
    if (!silent) setCouponLoading(true);
    setCouponError(false);
    
    try {
      // When drilling down into a validator, always fetch complete dataset
      // Users expect full details when they specifically select a validator to analyze
      const shouldFetchAll = true; // Always get complete data for drill-down analysis
      
      const fetchTake = 10000; // Large number to get all items for this validator
      const fetchSkip = 0; // Start from beginning
      
      const response = await dashboardAPI.getModeratorCouponRedemptions(
        moderatorName,
        fetchSkip,
        fetchTake,
        'moderated_at',
        'desc',
        '',
        false,
        dateRange
      );
      
      const result = response.data;
      
      // Map API response to component format
      const couponData = (result.items || []).map(item => {
        // Calculate differences with better handling of missing values
        let calculatedDiscount = item.calculatedDiscount;
        const discountApplied = item.discountApplied || 0;
        const detectedDiscount = item.detectedDiscount || 0;
        
        // If calculatedDiscount is missing or null, use the coupon amount as the expected value
        if (calculatedDiscount === null || calculatedDiscount === undefined) {
          calculatedDiscount = item.amount || 0;
        }

        // Treat null/missing values as 0
        const discountClaimed = discountApplied || 0;
        let estimatedDue = calculatedDiscount || 0;

        // VAT correction heuristic: if AI estimate excl. VAT times 1.15 is within R10 of claimed,
        // assume data typo and align AI estimate to claimed for UI purposes
        if (estimatedDue > 0 && discountClaimed > 0) {
          const vatAdjustedEstimate = estimatedDue * 1.15;
          if (Math.abs(vatAdjustedEstimate - discountClaimed) <= 10.0) {
            calculatedDiscount = discountApplied;
            estimatedDue = calculatedDiscount;
          }
        }

        // VAT correction heuristic for detected_discount: if AI detected excl. VAT times 1.15 is within R10 of claimed,
        // assume data typo and align AI detected to claimed for UI purposes
        let adjustedDetectedDiscount = detectedDiscount || 0;
        if (adjustedDetectedDiscount > 0 && discountClaimed > 0) {
          const vatAdjustedDetected = adjustedDetectedDiscount * 1.15;
          if (Math.abs(vatAdjustedDetected - discountClaimed) <= 10.0) {
            adjustedDetectedDiscount = discountApplied;
          }
        }

        // Calculate status based on business logic (5 rand tolerance)
        let status = 'Correct';
        
        const actuallyGiven = adjustedDetectedDiscount;
        
        // Check for invalid transactions first
        if (!calculatedDiscount && discountApplied === 0) {
          status = 'Invalid';
        } else {
          // Primary check: Claimed vs Actually Given (5 rand tolerance)
          const claimedVsGivenDiff = Math.abs(discountClaimed - actuallyGiven);
          if (claimedVsGivenDiff > 5.00) {
            if (discountClaimed > actuallyGiven) {
              status = 'Under Paid';  // Vet claimed more than actually given - customer underpaid
            } else {
              status = 'Over Paid';   // Vet claimed less than actually given - customer overpaid
            }
          } else {
            // Secondary check: AI Estimate vs Claimed (5 rand tolerance)
            const estimateVsClaimedDiff = Math.abs(estimatedDue - discountClaimed);
            if (estimateVsClaimedDiff > 5.00) {
              if (estimatedDue > discountClaimed) {
                status = 'Discount Calc Too Low';   // AI thinks should be higher
              } else {
                status = 'Discount Calc Too High';  // AI thinks should be lower
              }
            } else {
              status = 'Correct';  // Everything matches (green tick)
            }
          }
        }
        
        // Determine if there are significant differences (> 1 cent tolerance)
        const hasCalculationError = Math.abs(calculatedDiscount - discountApplied) > 0.01;
        const hasDetectionError = Math.abs(adjustedDetectedDiscount - discountApplied) > 0.01;
        
        // Build combined difference display
        let combinedDifference = [];
        if (hasCalculationError) {
          combinedDifference.push(calculatedDiscount - discountApplied);
        }
        if (hasDetectionError) {
          combinedDifference.push(adjustedDetectedDiscount - discountApplied);
        }
        
        // Use the first difference for backward compatibility, but store both
        let difference = hasCalculationError ? calculatedDiscount - discountApplied : adjustedDetectedDiscount - discountApplied;
        
        // Find primary product (Hills brand if available, otherwise first product)
        const primaryProduct = item.products?.find(p => p.brand === 'HILLS') || item.products?.[0] || {
          name: 'No products found',
          brand: 'UNKNOWN',
          category: 'UNKNOWN',
          species: null,
          life_stage: null,
          cost: 0
        };
        
        // Count Hills products
        const hillsProductCount = item.products?.filter(p => p.brand === 'HILLS').length || 0;

        // Derived rule flags
        const belowMinSpend = typeof item.minSpend === 'number' && typeof item.basketCost === 'number'
          ? item.basketCost < item.minSpend
          : false;
        const exceedsMaxDiscount = typeof item.maxDiscount === 'number' && typeof discountApplied === 'number'
          ? discountApplied > item.maxDiscount
          : false;
        const noHillsItem = hillsProductCount === 0;

        // Reason list for chips
        const reasons = [];
        if (Math.abs((discountApplied || 0) - (adjustedDetectedDiscount || 0)) > 5.0) {
          const delta = (discountApplied || 0) - (adjustedDetectedDiscount || 0);
          reasons.push(delta > 0 ? `Claimed > Paid out by R${Math.abs(delta).toFixed(2)}` : `Paid out > Claimed by R${Math.abs(delta).toFixed(2)}`);
        }
        // Only include AI expected reason if it truly differs after VAT correction
        if (Math.abs((calculatedDiscount || 0) - (discountApplied || 0)) > 5.0) {
          reasons.push(`AI expected R${(calculatedDiscount || 0).toFixed(2)}`);
        }
        if (belowMinSpend) reasons.push('Below min spend');
        if (exceedsMaxDiscount) reasons.push('Exceeds max discount');
        if (noHillsItem) reasons.push('No HILLS item');
        
        return {
          // API fields mapped to component expected format
          coupon_metadata_id: item.id,
          actual_code: item.actualCode,
          code: item.code,
          type: item.type,
          state: item.state,
          campaign_name: item.campaignName,
          invoice_uri: item.invoiceUri,
          staff_member: item.staffMember,
          retailer: item.retailer,
          retail_group: item.retailGroup,
          moderator: item.moderator || selectedModerator?.name,
          min_spend: item.minSpend,
          max_discount: item.maxDiscount,
          amount: item.amount,
          basket_cost: item.basketCost,
          discount_applied: item.discountApplied,
          calculated_discount: calculatedDiscount,
          detected_discount: adjustedDetectedDiscount,
          basket_size: item.basketSize,
          is_legacy: item.isLegacy,
          is_test: item.isTest,
          is_default_staff_member: item.isDefaultStaffMember,
          is_over_paid: item.isOverPaid,
          is_under_paid: item.isUnderPaid,
          redeemed_at: item.redeemedAt,
          moderated_at: item.moderatedAt,
          normalized_moderated_at: item.normalizedModeratedAt,
          products: item.products || [],
          reasons,
          flags: { belowMinSpend, exceedsMaxDiscount, noHillsItem },
          // Computed fields for UI
          status: status,
          difference: difference,
          combinedDifference: combinedDifference,
          calculationDifference: calculatedDiscount - discountApplied,
          detectionDifference: adjustedDetectedDiscount - discountApplied,
          hasCalculationError: hasCalculationError,
          hasDetectionError: hasDetectionError,
          primaryProduct: primaryProduct,
          hillsProductCount: hillsProductCount
        };
      });
      
      setCouponRedemptions(couponData);
      setCouponPagination({
        pageNumber: result.pageNumber || couponCurrentPage,
        pageSize: result.pageSize || couponPageSize,
        total: result.total || 0,
        pageCount: result.pageCount || 0
      });
      setCouponError(false);
      
    } catch (error) {
      console.error('Error fetching validator coupon redemptions:', error);
      setCouponError(true);
    } finally {
      if (!silent) setCouponLoading(false);
    }
  }, [couponCurrentPage, couponPageSize, dateRange, selectedModerator?.name, statusFilter]);

  const handleRevalidateCouponMetadata = useCallback(async (coupon) => {
    const id = coupon.coupon_metadata_id;
    if (!id) return;

    setRevalidatingCouponId(id);
    try {
      await dashboardAPI.revalidateCouponRedemption(id);
      if (typeof window !== 'undefined' && window.M && typeof window.M.toast === 'function') {
        window.M.toast({ html: 'Coupon metadata has been revalidated.', displayLength: 4000 });
      }
      if (selectedModerator?.name) {
        await fetchCouponRedemptions(selectedModerator.name, { silent: true });
      }
    } catch (error) {
      const data = error.response?.data;
      let errText = error.message || 'Could not revalidate coupon metadata.';
      if (typeof data?.detail === 'string') {
        errText = data.detail;
      } else if (Array.isArray(data?.errors) && data.errors.length) {
        errText = data.errors.map((e) => e.message || e).join(' ');
      }
      if (typeof window !== 'undefined' && window.M && typeof window.M.toast === 'function') {
        window.M.toast({ html: errText, classes: 'red', displayLength: 6000 });
      }
    } finally {
      setRevalidatingCouponId(null);
    }
  }, [selectedModerator, fetchCouponRedemptions]);

  const handleDrillDown = useCallback((moderatorName) => {
    const moderator = allModerators.find(m => m.name === moderatorName);
    
    // Check if moderator is unassigned
    if (moderator?.name === 'Not Assigned') {
      console.warn('Cannot drill down on unassigned redemptions');
      return;
    }
    
    setSelectedModerator(moderator);
    setDrillDownOpen(true);
    
    if (moderator?.name) {
      fetchCouponRedemptions(moderator.name);
    }
  }, [allModerators, fetchCouponRedemptions]);

  // Register drill-down trigger with parent component
  useEffect(() => {
    if (onRegisterDrillDownTrigger) {
      onRegisterDrillDownTrigger('validator', handleDrillDown);
    }
  }, [onRegisterDrillDownTrigger, handleDrillDown]);

  // Coupon pagination handlers
  const handleCouponPageChange = (newPage) => {
    // Calculate effective page count based on current filter state
    const effectivePageCount = statusFilter 
      ? Math.ceil(allFilteredCouponRedemptions.length / couponPageSize)
      : couponPagination.pageCount;
      
    if (newPage >= 1 && newPage <= effectivePageCount) {
      setCouponCurrentPage(newPage);
    }
  };

  const handleCouponPageSizeChange = (size) => {
    setCouponPageSize(size);
    setCouponCurrentPage(1);
  };

  // Handle basket details
  const showBasketDetails = (coupon) => {
    setSelectedBasket(coupon);
    setBasketDetailsOpen(true);
  };

  const closeBasketDetails = () => {
    setBasketDetailsOpen(false);
    setSelectedBasket(null);
  };

  // Analytics data processing functions
  const processAnalyticsData = useCallback((couponData) => {
    if (!couponData || couponData.length === 0) return;

    // Group by SAST moderation time (normalizedModeratedAt when available)
    const dailyData = {};
    const weeklyData = {};
    const hourlyData = {};
    const dayOfWeekData = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0, 6: 0 };
    const errorTypeData = {};
    const retailerData = {};

    couponData.forEach(coupon => {
      const chartIso = firstPlausibleDate(
        coupon.normalized_moderated_at,
        coupon.moderated_at,
        coupon.redeemed_at
      );
      if (!chartIso) return;

      const bucket = sastBucket(chartIso);
      const date = bucket.dateKey;
      const hour = bucket.hour;
      const dayOfWeek = bucket.dayOfWeek;
      const weekKey = bucket.weekStartKey;
      const retailer = coupon.retailer || 'Unknown';

      // Daily trends
      if (!dailyData[date]) {
        dailyData[date] = { 
          date, 
          slipsValidated: 0, 
          correct: 0, 
          errors: 0, 
          totalProcessingTime: 0,
          errorAmount: 0,
          validationDelays: [] // Track time between redemption and moderation
        };
      }
      dailyData[date].slipsValidated++;
      if (coupon.status === 'Correct') {
        dailyData[date].correct++;
      } else {
        dailyData[date].errors++;
        dailyData[date].errorAmount += Math.abs((coupon.calculated_discount || 0) - (coupon.discount_applied || 0));
      }

      // Weekly trends
      if (!weeklyData[weekKey]) {
        weeklyData[weekKey] = {
          weekStart: weekKey,
          slipsValidated: 0,
          correct: 0,
          errors: 0,
          validationDelays: []
        };
      }
      weeklyData[weekKey].slipsValidated++;
      if (coupon.status === 'Correct') {
        weeklyData[weekKey].correct++;
      } else {
        weeklyData[weekKey].errors++;
      }

      if (isPlausibleDate(coupon.moderated_at) && isPlausibleDate(coupon.redeemed_at)) {
        const redeemedTime = new Date(coupon.redeemed_at).getTime();
        const moderatedTime = new Date(coupon.moderated_at).getTime();
        const delayHours = (moderatedTime - redeemedTime) / (1000 * 60 * 60);
        if (delayHours >= 0) {
          dailyData[date].validationDelays.push(delayHours);
          weeklyData[weekKey].validationDelays.push(delayHours);
        }
      }

      // Hourly activity
      if (!hourlyData[hour]) hourlyData[hour] = 0;
      hourlyData[hour]++;
      dayOfWeekData[dayOfWeek] = (dayOfWeekData[dayOfWeek] || 0) + 1;

      // Error patterns
      if (coupon.status !== 'Correct') {
        if (!errorTypeData[coupon.status]) errorTypeData[coupon.status] = [];
        errorTypeData[coupon.status].push({
          date,
          amount: Math.abs((coupon.calculated_discount || 0) - (coupon.discount_applied || 0)),
          retailer,
          reasons: coupon.reasons || []
        });
      }

      // Retailer performance
      if (!retailerData[retailer]) {
        retailerData[retailer] = { retailer, total: 0, correct: 0, errors: 0 };
      }
      retailerData[retailer].total++;
      if (coupon.status === 'Correct') {
        retailerData[retailer].correct++;
      } else {
        retailerData[retailer].errors++;
      }
    });

    // Calculate daily accuracy and validation delays
    const dailyTrends = Object.values(dailyData).map(day => {
      const avgValidationDelay = day.validationDelays.length > 0 
        ? day.validationDelays.reduce((sum, delay) => sum + delay, 0) / day.validationDelays.length
        : 0;
      
      return {
        ...day,
        accuracy: day.slipsValidated > 0 ? (day.correct / day.slipsValidated) * 100 : 0,
        errorRate: day.slipsValidated > 0 ? (day.errors / day.slipsValidated) * 100 : 0,
        avgProcessingTime: Math.random() * 30 + 60, // Simulated - would come from API
        avgValidationDelay: avgValidationDelay, // Average hours between redemption and validation
        validationDelayCount: day.validationDelays.length
      };
    }).sort((a, b) => a.date.localeCompare(b.date));

    // Calculate weekly accuracy and validation delays
    const weeklyTrends = Object.values(weeklyData).map(week => {
      const avgValidationDelay = week.validationDelays.length > 0 
        ? week.validationDelays.reduce((sum, delay) => sum + delay, 0) / week.validationDelays.length
        : 0;
      
      return {
        weekStart: week.weekStart,
        slipsValidated: week.slipsValidated,
        correct: week.correct,
        errors: week.errors,
        accuracy: week.slipsValidated > 0 ? (week.correct / week.slipsValidated) * 100 : 0,
        errorRate: week.slipsValidated > 0 ? (week.errors / week.slipsValidated) * 100 : 0,
        avgValidationDelay: avgValidationDelay,
        validationDelayCount: week.validationDelays.length
      };
    }).sort((a, b) => a.weekStart.localeCompare(b.weekStart));

    // Process error patterns
    const errorPatterns = Object.entries(errorTypeData).map(([type, errors]) => ({
      type,
      count: errors.length,
      totalAmount: errors.reduce((sum, e) => sum + e.amount, 0),
      avgAmount: errors.reduce((sum, e) => sum + e.amount, 0) / errors.length,
      dailyBreakdown: errors.reduce((acc, error) => {
        acc[error.date] = (acc[error.date] || 0) + 1;
        return acc;
      }, {}),
      commonReasons: errors.flatMap(e => e.reasons).reduce((acc, reason) => {
        acc[reason] = (acc[reason] || 0) + 1;
        return acc;
      }, {})
    }));

    // Process workload data
    const workloadData = {
      hourlyActivity: Object.entries(hourlyData).map(([hour, count]) => ({
        hour: parseInt(hour),
        count,
        label: `${hour}:00`
      })).sort((a, b) => a.hour - b.hour),
      dayOfWeekActivity: [0,1,2,3,4,5,6].map(d => ({
        day: d,
        label: ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d],
        count: dayOfWeekData[d] || 0
      })),
      batchPatterns: [],
      teamComparison: []
    };

    // Process retailer performance
    const retailerPerformance = Object.values(retailerData).map(retailer => ({
      ...retailer,
      accuracy: retailer.total > 0 ? (retailer.correct / retailer.total) * 100 : 0
    })).sort((a, b) => b.accuracy - a.accuracy);

    setAnalyticsData({
      dailyTrends,
      weeklyTrends,
      errorPatterns,
      workloadData,
      qualityInsights: [], // Would be processed from more detailed data
      coachingData: [], // Would be processed from error analysis
      peerComparison: [], // Would come from API
      retailerPerformance
    });
  }, []);

  // Fetch analytics data when validator is selected
  const fetchAnalyticsData = useCallback(async (validatorId) => {
    if (!validatorId || activeTab === 'overview') return;
    
    setAnalyticsLoading(true);
    try {
      // For now, use the existing coupon data for analytics
      // In a real implementation, you'd call specific analytics APIs
      processAnalyticsData(couponRedemptions);
    } catch (error) {
      console.error('Error processing analytics data:', error);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [activeTab, couponRedemptions, processAnalyticsData]);

  // Handle invoice preview
  const showInvoicePreview = (coupon) => {
    setSelectedInvoice(coupon);
    setInvoicePreviewOpen(true);
  };

  const closeInvoicePreview = () => {
    setInvoicePreviewOpen(false);
    setSelectedInvoice(null);
    // Reset image controls when closing
    setImageRotation(0);
    setImageZoom(1);
    setImagePanX(0);
    setImagePanY(0);
    setIsDragging(false);
  };

  // Image control handlers
  const handleImageRotate = () => {
    setImageRotation(prev => (prev - 90 + 360) % 360);
    // Reset pan when rotating
    setImagePanX(0);
    setImagePanY(0);
  };
  
  const handleImageZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 0.25, 3));
  };
  
  const handleImageZoomOut = () => {
    setImageZoom(prev => {
      const newZoom = Math.max(prev - 0.25, 0.25);
      // Reset pan when zooming out to fit
      if (newZoom <= 1) {
        setImagePanX(0);
        setImagePanY(0);
      }
      return newZoom;
    });
  };

  // Pan/drag handlers
  const handleImageMouseDown = (e) => {
    if (imageZoom > 1) {
      setIsDragging(true);
      // Store the initial mouse position and current pan position
      setDragStart({
        x: e.clientX,
        y: e.clientY,
        panX: imagePanX,
        panY: imagePanY
      });
      e.preventDefault();
    }
  };

  const handleImageMouseMove = (e) => {
    if (isDragging && imageZoom > 1) {
      // Calculate the mouse movement delta
      const deltaX = e.clientX - dragStart.x;
      const deltaY = e.clientY - dragStart.y;
      
      // Convert rotation angle to radians
      const angleRad = (imageRotation * Math.PI) / 180;
      
      // Rotate the delta vector by the negative of the image rotation
      // This transforms screen-space movement to image-space movement
      const cosAngle = Math.cos(-angleRad);
      const sinAngle = Math.sin(-angleRad);
      
      const rotatedDeltaX = deltaX * cosAngle - deltaY * sinAngle;
      const rotatedDeltaY = deltaX * sinAngle + deltaY * cosAngle;
      
      // Apply the rotated delta to the original pan position
      setImagePanX(dragStart.panX + rotatedDeltaX);
      setImagePanY(dragStart.panY + rotatedDeltaY);
    }
  };

  const handleImageMouseUp = () => {
    setIsDragging(false);
  };

  const handleImageMouseLeave = () => {
    setIsDragging(false);
  };
  
  const handleImageFullscreen = () => {
    if (selectedInvoice?.invoice_uri) {
      window.open(selectedInvoice.invoice_uri, '_blank');
    }
  };

  // Determine invoice type from URI
  const getInvoiceType = (uri) => {
    if (!uri) return 'unknown';
    const extension = uri.split('.').pop()?.toLowerCase();
    if (['pdf'].includes(extension)) return 'pdf';
    if (['jpg', 'jpeg', 'jpe', 'png', 'gif', 'webp'].includes(extension)) return 'image';
    return 'unknown';
  };

  // Update coupon data when pagination changes
  useEffect(() => {
    if (drillDownOpen && selectedModerator?.name) {
      fetchCouponRedemptions(selectedModerator.name);
    }
  }, [couponCurrentPage, couponPageSize, drillDownOpen, selectedModerator, fetchCouponRedemptions]);

  // Process analytics data when coupon data changes
  useEffect(() => {
    if (couponRedemptions.length > 0 && activeTab !== 'overview') {
      processAnalyticsData(couponRedemptions);
    }
  }, [couponRedemptions, activeTab, processAnalyticsData]);

  // Data formatters
  const formatPercentage = (value) => `${(value || 0).toFixed(1)}%`;
  const formatNumber = (value) => (value || 0).toLocaleString();
  const formatCurrency = (value) => `R${(value ?? 0).toFixed(2)}`;
  const formatRelativeDate = (dateStr) => {
    try {
      const date = new Date(dateStr);
      return date.toLocaleString();
    } catch {
      return new Date(dateStr).toLocaleString();
    }
  };

  // Prepare data for charts using ALL data (not just current page)
  const moderatorNames = allModerators.map(m => m.name || 'Unknown');
  const moderatorAccuracies = allModerators.map(m => isNaN(m.accuracy) ? 0 : (m.accuracy || 0));
  
  // Calculate volume percentages using all data
  const totalReviewed = allModerators.reduce((sum, m) => sum + (m.reviewed || 0), 0);
  const moderatorVolumes = totalReviewed > 0 
    ? allModerators.map(m => {
        const percentage = ((m.reviewed || 0) / totalReviewed) * 100;
        return isNaN(percentage) ? 0 : parseFloat(percentage.toFixed(1));
      })
    : allModerators.map(() => 0);

  // Chart configurations
  const accuracyChartData = moderatorAccuracies.length > 0 
    ? [{ 
        name: 'Accuracy (%)', 
        data: moderatorAccuracies 
      }]
    : [{ 
        name: 'Accuracy (%)', 
        data: [0]
      }];

  const accuracyChartCategories = moderatorNames.length > 0 
    ? moderatorNames 
    : ['No Data'];

  const accuracyChartOptions = {
    chart: { 
      type: 'bar', 
      height: 400, 
      background: '#FFFFFF',
      toolbar: {
        show: false
      }
    },
    xaxis: { 
      categories: accuracyChartCategories,
      labels: {
        rotate: -45,
        style: {
          fontSize: '12px'
        }
      }
    },
    colors: [chartColors.primary],
    title: { 
      text: 'Validator Accuracy (%)',
      style: {
        fontSize: '16px',
        fontWeight: 'normal'
      }
    },
    plotOptions: {
      bar: {
        dataLabels: {
          position: 'top'
        }
      }
    },
    dataLabels: {
      enabled: true,
      formatter: function (val) {
        return parseFloat(val).toFixed(2) + "%";
      },
      offsetY: -20,
      style: {
        fontSize: '12px',
        colors: ["#304758"]
      }
    },
    yaxis: {
      labels: {
        formatter: function (val) {
          return parseFloat(val).toFixed(2) + "%";
        }
      }
    },
    noData: {
      text: 'Loading...',
      align: 'center',
      verticalAlign: 'middle'
    }
  };

  const volumeChartData = moderatorVolumes.length > 0 && moderatorVolumes.some(v => v > 0)
    ? moderatorVolumes
    : [1]; // Default data to prevent empty series

  const volumeChartLabels = moderatorNames.length > 0 && moderatorVolumes.some(v => v > 0)
    ? moderatorNames
    : ['No Data'];

  const volumeChartOptions = {
    chart: { 
      type: 'pie', 
      height: 400, 
      background: '#FFFFFF' 
    },
    labels: volumeChartLabels,
    colors: [
      chartColors.primary,
      chartColors.success,
      chartColors.secondary,
      chartColors.info,
      chartColors.light,
      chartColors.danger
    ],
    title: { 
      text: 'Volume of Slips Reviewed (%)',
      style: {
        fontSize: '16px',
        fontWeight: 'normal'
      }
    },
    legend: {
      position: 'right'
    },
    responsive: [{
      breakpoint: 480,
      options: {
        chart: {
          width: 200
        },
        legend: {
          position: 'right'
        }
      }
    }],
    noData: {
      text: 'Loading...',
      align: 'center',
      verticalAlign: 'middle'
    }
  };

  return (
    <div className="section-box">
      <div className="section-title">Validator Performance Insights</div>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="preloader-wrapper active">
            <div className="spinner-layer spinner-blue-only">
              <div className="circle-clipper left">
                <div className="circle"></div>
              </div>
              <div className="gap-patch">
                <div className="circle"></div>
              </div>
              <div className="circle-clipper right">
                <div className="circle"></div>
              </div>
            </div>
          </div>
          <p>Loading validator performance data...</p>
        </div>
      ) : (
        <>
          {error && (
            <div style={{ backgroundColor: '#fff3cd', border: '1px solid #ffeaa7', padding: '10px', marginBottom: '20px', borderRadius: '4px' }}>
              <i className="fas fa-exclamation-triangle" style={{ color: '#856404', marginRight: '8px' }}></i>
              <span style={{ color: '#856404' }}>Using fallback data - API connection failed</span>
            </div>
          )}
          
          {/* Error or Empty State */}
          {allModerators.length === 0 && !loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              <i className="fas fa-info-circle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
              <p>No validator data available for selected date range</p>
            </div>
          ) : (
            <>
              {/* Data Summary */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' }}>
                <div style={{ 
                  backgroundColor: '#ffffff', 
                  borderRadius: '12px', 
                  padding: '20px', 
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                  border: '1px solid #e9ecef',
                  textAlign: 'center' 
                }}>
                  <div style={{ 
                    width: '40px', 
                    height: '40px', 
                    borderRadius: '50%', 
                    backgroundColor: '#002E6D', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    margin: '0 auto 15px auto' 
                  }}>
                    <i className="fas fa-users" style={{ color: 'white', fontSize: '18px' }}></i>
                  </div>
                  <div style={{ color: '#6c757d', fontSize: '14px', marginBottom: '5px' }}>Total Validators</div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>{formatNumber(totalModerators)}</div>
                </div>
                
                <div style={{ 
                  backgroundColor: '#ffffff', 
                  borderRadius: '12px', 
                  padding: '20px', 
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                  border: '1px solid #e9ecef',
                  textAlign: 'center' 
                }}>
                  <div style={{ 
                    width: '40px', 
                    height: '40px', 
                    borderRadius: '50%', 
                    backgroundColor: '#4CAF50', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    margin: '0 auto 15px auto' 
                  }}>
                    <i className="fas fa-clipboard-list" style={{ color: 'white', fontSize: '18px' }}></i>
                  </div>
                  <div style={{ color: '#6c757d', fontSize: '14px', marginBottom: '5px' }}>Total Reviews</div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>{formatNumber(totalReviewed)}</div>
                </div>
                
                <div style={{ 
                  backgroundColor: '#ffffff', 
                  borderRadius: '12px', 
                  padding: '20px', 
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)', 
                  border: '1px solid #e9ecef',
                  textAlign: 'center' 
                }}>
                  <div style={{ 
                    width: '40px', 
                    height: '40px', 
                    borderRadius: '50%', 
                    backgroundColor: '#fd7e14', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    margin: '0 auto 15px auto' 
                  }}>
                    <i className="fas fa-chart-line" style={{ color: 'white', fontSize: '18px' }}></i>
                  </div>
                  <div style={{ color: '#6c757d', fontSize: '14px', marginBottom: '5px' }}>Average Accuracy</div>
                  <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#333' }}>
                    {validatorAccuracyLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <div className="preloader-wrapper small active">
                          <div className="spinner-layer spinner-orange-only">
                            <div className="circle-clipper left">
                              <div className="circle"></div>
                            </div>
                            <div className="gap-patch">
                              <div className="circle"></div>
                            </div>
                            <div className="circle-clipper right">
                              <div className="circle"></div>
                            </div>
                          </div>
                        </div>
                        <span style={{ fontSize: '16px' }}>Loading...</span>
                      </div>
                    ) : validatorAccuracyError ? (
                      <span style={{ color: '#dc3545', fontSize: '16px' }}>Error</span>
                    ) : (
                      formatPercentage(validatorAccuracy || 0)
                    )}
                  </div>
                </div>
              </div>

              {/* Validator Performance Table */}
              <table className="striped">
                <thead>
                  <tr>
                    <th 
                      onClick={() => handleSort('name')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Validator"
                    >
                      Validator{getSortIndicator('name')}
                    </th>
                    <th 
                      onClick={() => handleSort('reviewed')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Slips Reviewed"
                    >
                      Slips Reviewed{getSortIndicator('reviewed')}
                    </th>
                    <th 
                      onClick={() => handleSort('correct')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Correct Reviews"
                    >
                      Correct Reviews{getSortIndicator('correct')}
                    </th>
                    <th 
                      onClick={() => handleSort('accuracy')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Accuracy"
                    >
                      Accuracy{getSortIndicator('accuracy')}
                    </th>
                    <th 
                      onClick={() => handleSort('inaccurate')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Inaccurate Reviews"
                    >
                      Inaccurate Reviews{getSortIndicator('inaccurate')}
                    </th>
                    <th 
                      onClick={() => handleSort('overPaid')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Over Paid"
                    >
                      Over Paid{getSortIndicator('overPaid')}
                    </th>
                    <th 
                      onClick={() => handleSort('underPaid')}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title="Click to sort by Under Paid"
                    >
                      Under Paid{getSortIndicator('underPaid')}
                    </th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {moderators.map((moderator, index) => (
                    <tr key={moderator.name || index}>
                      <td>{moderator.name}</td>
                      <td>{formatNumber(moderator.reviewed)}</td>
                      <td>{formatNumber(moderator.correct)}</td>
                      <td>{formatPercentage(moderator.accuracy)}</td>
                      <td>{formatNumber(moderator.inaccurate)}</td>
                      <td>{formatNumber(moderator.overPaid)}</td>
                      <td>{formatNumber(moderator.underPaid)}</td>
                      <td>
                        {moderator.name === 'Not Assigned' ? (
                          <span 
                            style={{
                              padding: '6px 12px',
                              fontSize: '12px',
                              color: '#6c757d',
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              borderRadius: '4px',
                              cursor: 'help'
                            }}
                            title="Cannot drill down on unassigned redemptions"
                          >
                            Unassigned
                          </span>
                        ) : (
                          <button 
                            className="drill-btn"
                            onClick={() => handleDrillDown(moderator.name)}
                          >
                            Drill Down
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Client-side Pagination Controls */}
              {totalModerators > 0 && (
                <div style={{ marginTop: '20px' }}>
                  {/* Pagination Info */}
                  <div style={{ textAlign: 'center', color: '#666', marginBottom: '15px' }}>
                    <p>Showing {moderators.length} of {formatNumber(totalModerators)} validators</p>
                    {totalPages > 1 && (
                      <p>Page {currentPage} of {totalPages}</p>
                    )}
                  </div>
                  
                  {/* Pagination Controls */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    {/* Page Size Selector */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: '15px',
                      backgroundColor: '#f8f9fa',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '2px solid #e9ecef',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}>
                      <label style={{ 
                        fontSize: '15px', 
                        color: '#495057',
                        fontWeight: '600',
                        whiteSpace: 'nowrap'
                      }}>
                        Items per page:
                      </label>
                      
                      <div style={{ display: 'flex', gap: '8px' }}>
                        {[10, 25, 50, 100].map(size => (
                          <button
                            key={size}
                            onClick={() => handlePageSizeChange(size)}
                            style={{
                              padding: '8px 16px',
                              border: pageSize === size ? '2px solid #007bff' : '2px solid #ced4da',
                              borderRadius: '6px',
                              backgroundColor: pageSize === size ? '#007bff' : '#ffffff',
                              color: pageSize === size ? '#ffffff' : '#495057',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500',
                              transition: 'all 0.2s ease',
                              minWidth: '50px'
                            }}
                            onMouseOver={(e) => {
                              if (pageSize !== size) {
                                e.target.style.backgroundColor = '#e9ecef';
                                e.target.style.borderColor = '#007bff';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (pageSize !== size) {
                                e.target.style.backgroundColor = '#ffffff';
                                e.target.style.borderColor = '#ced4da';
                              }
                            }}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                    </div>
                    
                    {/* Page Navigation */}
                    {totalPages > 1 && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <button 
                          onClick={() => handlePageChange(currentPage - 1)}
                          disabled={currentPage === 1}
                          style={{ 
                            padding: '8px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: '4px',
                            backgroundColor: currentPage === 1 ? '#f5f5f5' : '#fff',
                            color: currentPage === 1 ? '#999' : '#333',
                            cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Previous
                        </button>
                        
                        {/* Page Numbers */}
                        <div style={{ display: 'flex', gap: '5px' }}>
                          {/* First page */}
                          {currentPage > 3 && (
                            <>
                              <button 
                                onClick={() => handlePageChange(1)}
                                style={{ 
                                  padding: '8px 12px', 
                                  border: '1px solid #ddd', 
                                  borderRadius: '4px',
                                  backgroundColor: '#fff',
                                  color: '#333',
                                  cursor: 'pointer',
                                  fontSize: '14px'
                                }}
                              >
                                1
                              </button>
                              {currentPage > 4 && <span style={{ padding: '8px 4px', color: '#999' }}>...</span>}
                            </>
                          )}
                          
                          {/* Pages around current */}
                          {[...Array(totalPages)].map((_, index) => {
                            const pageNum = index + 1;
                            if (pageNum >= currentPage - 2 && pageNum <= currentPage + 2) {
                              return (
                                <button 
                                  key={pageNum}
                                  onClick={() => handlePageChange(pageNum)}
                                  style={{ 
                                    padding: '8px 12px', 
                                    border: '1px solid #ddd', 
                                    borderRadius: '4px',
                                    backgroundColor: pageNum === currentPage ? '#007bff' : '#fff',
                                    color: pageNum === currentPage ? '#fff' : '#333',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: pageNum === currentPage ? 'bold' : 'normal'
                                  }}
                                >
                                  {pageNum}
                                </button>
                              );
                            }
                            return null;
                          })}
                          
                          {/* Last page */}
                          {currentPage < totalPages - 2 && (
                            <>
                              {currentPage < totalPages - 3 && <span style={{ padding: '8px 4px', color: '#999' }}>...</span>}
                              <button 
                                onClick={() => handlePageChange(totalPages)}
                                style={{ 
                                  padding: '8px 12px', 
                                  border: '1px solid #ddd', 
                                  borderRadius: '4px',
                                  backgroundColor: '#fff',
                                  color: '#333',
                                  cursor: 'pointer',
                                  fontSize: '14px'
                                }}
                              >
                                {totalPages}
                              </button>
                            </>
                          )}
                        </div>
                        
                        <button 
                          onClick={() => handlePageChange(currentPage + 1)}
                          disabled={currentPage === totalPages}
                          style={{ 
                            padding: '8px 12px', 
                            border: '1px solid #ddd', 
                            borderRadius: '4px',
                            backgroundColor: currentPage === totalPages ? '#f5f5f5' : '#fff',
                            color: currentPage === totalPages ? '#999' : '#333',
                            cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          Next
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Validator Charts - Using ALL data for comprehensive visualization */}
          {allModerators.length > 0 && moderatorAccuracies.some(acc => acc > 0) && (
            <div className="row" style={{ marginTop: '30px' }}>
              <div className="col s12">
                <h5>Validator Accuracy (%)</h5>
                <div className="chart-container">
                  <Chart
                    options={accuracyChartOptions}
                    series={accuracyChartData}
                    type="bar"
                    height={400}
                  />
                </div>
              </div>
              <div className="col s12">
                <h5>Volume of Slips Reviewed</h5>
                <div className="chart-container">
                  <Chart
                    options={volumeChartOptions}
                    series={volumeChartData}
                    type="pie"
                    height={400}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Drill Down Dialog */}
      {drillDownOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: '#ffffff',
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto'
        }}>
          {/* Header with back and close buttons */}
          <div style={{
            position: 'sticky',
            top: 0,
            backgroundColor: '#ffffff',
            borderBottom: '2px solid #e9ecef',
            padding: '20px 30px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            zIndex: 10000
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
              <button
                onClick={handleCloseDrillDown}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '20px',
                  cursor: 'pointer',
                  color: '#666',
                  padding: '8px',
                  borderRadius: '50%',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '40px',
                  height: '40px'
                }}
                onMouseOver={(e) => {
                  e.target.style.backgroundColor = '#f8f9fa';
                  e.target.style.color = '#333';
                }}
                onMouseOut={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#666';
                }}
                title="Back"
              >
                <i className="fas fa-chevron-left"></i>
              </button>
              <div>
                <h4 style={{ margin: 0, color: '#333', fontSize: '24px', fontWeight: '600' }}>
                  Validator Performance Details
                </h4>
                {selectedModerator && (
                  <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '16px' }}>
                    {selectedModerator.name}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleCloseDrillDown}
              style={{
                background: 'none',
                border: 'none',
                fontSize: '28px',
                cursor: 'pointer',
                color: '#666',
                padding: '10px',
                borderRadius: '50%',
                transition: 'all 0.2s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '50px',
                height: '50px'
              }}
              onMouseOver={(e) => {
                e.target.style.backgroundColor = '#f8f9fa';
                e.target.style.color = '#333';
              }}
              onMouseOut={(e) => {
                e.target.style.backgroundColor = 'transparent';
                e.target.style.color = '#666';
              }}
              title="Close"
            >
              ×
            </button>
          </div>

          {/* Tab Navigation */}
          <div style={{
            backgroundColor: '#ffffff',
            borderBottom: '1px solid #e9ecef',
            padding: '0 30px',
            display: 'flex',
            gap: '0'
          }}>
            {[
              { id: 'overview', label: 'Overview', icon: 'fas fa-chart-line' },
              // { id: 'daily-trends', label: 'Daily Trends', icon: 'fas fa-chart-area' },
              { id: 'workload', label: 'Workload', icon: 'fas fa-clock' },
              { id: 'quality', label: 'Quality Insights', icon: 'fas fa-star' },
              { id: 'error-patterns', label: 'Error Analysis', icon: 'fas fa-exclamation-triangle' },
              { id: 'coaching', label: 'Coaching', icon: 'fas fa-user-graduate' },
              { id: 'share-invite', label: 'Share Invite', icon: 'fas fa-share-alt' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id !== 'overview') {
                    fetchAnalyticsData(selectedModerator?.name);
                  }
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '15px 20px',
                  cursor: 'pointer',
                  borderBottom: activeTab === tab.id ? '3px solid #007bff' : '3px solid transparent',
                  color: activeTab === tab.id ? '#007bff' : '#666',
                  fontSize: '14px',
                  fontWeight: activeTab === tab.id ? '600' : '500',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
                onMouseOver={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.color = '#333';
                    e.target.style.backgroundColor = '#f8f9fa';
                  }
                }}
                onMouseOut={(e) => {
                  if (activeTab !== tab.id) {
                    e.target.style.color = '#666';
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <i className={tab.icon} style={{ fontSize: '12px' }}></i>
                {tab.label}
              </button>
            ))}
          </div>

          {/* Content Area */}
          <div style={{
            flex: 1,
            padding: '30px',
            backgroundColor: '#f8f9fa'
          }}>
            {selectedModerator ? (
              <div>
                {/* Overview Tab Content */}
                {activeTab === 'overview' && (
                  <div>
                {/* Validator Summary Card */}
                <div style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '25px',
                  marginBottom: '25px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  border: '1px solid #e9ecef'
                }}>
                  <h5 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '20px', fontWeight: '600' }}>
                    Validator Overview
                  </h5>
                  {(() => {
                    // Calculate actual status counts from complete coupon dataset
                    // When drilling down into a validator, we always fetch all their data
                    const statusCounts = couponRedemptions.reduce((acc, coupon) => {
                      acc[coupon.status] = (acc[coupon.status] || 0) + 1;
                      return acc;
                    }, {});
                    
                    const actualOverPaid = statusCounts['Over Paid'] || 0;
                    const actualUnderPaid = statusCounts['Under Paid'] || 0;
                    const actualCorrect = statusCounts['Correct'] || 0;
                    const totalReviews = couponRedemptions.length;
                    // Calculate accuracy from actual coupon data, not from API
                    const calculatedAccuracy = totalReviews > 0 ? (actualCorrect / totalReviews) * 100 : 0;
                    
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                        <div 
                          onClick={() => handleStatusFilter(null)}
                          style={{ 
                            textAlign: 'center', 
                            padding: '15px', 
                            backgroundColor: statusFilter === null ? '#e3f2fd' : '#f8f9fa', 
                            borderRadius: '8px',
                            cursor: 'pointer',
                            border: statusFilter === null ? '2px solid #2196f3' : '2px solid transparent',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            if (statusFilter !== null) {
                              e.target.style.backgroundColor = '#e9ecef';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (statusFilter !== null) {
                              e.target.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                          title="Click to show all coupons"
                        >
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#007bff', marginBottom: '5px' }}>
                            {formatNumber(totalReviews)}
                          </div>
                          <div style={{ color: '#666', fontSize: '14px' }}>Total Reviews</div>
                        </div>
                        <div 
                          onClick={() => handleStatusFilter('Correct')}
                          style={{ 
                            textAlign: 'center', 
                            padding: '15px', 
                            backgroundColor: statusFilter === 'Correct' ? '#e8f5e8' : '#f8f9fa', 
                            borderRadius: '8px',
                            cursor: 'pointer',
                            border: statusFilter === 'Correct' ? '2px solid #4caf50' : '2px solid transparent',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            if (statusFilter !== 'Correct') {
                              e.target.style.backgroundColor = '#e9ecef';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (statusFilter !== 'Correct') {
                              e.target.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                          title="Click to show only correct coupons"
                        >
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#28a745', marginBottom: '5px' }}>
                            {formatNumber(actualCorrect)}
                          </div>
                          <div style={{ color: '#666', fontSize: '14px' }}>Correct Reviews</div>
                        </div>
                        <div style={{ textAlign: 'center', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#17a2b8', marginBottom: '5px' }}>
                            {formatPercentage(calculatedAccuracy)}
                          </div>
                          <div style={{ color: '#666', fontSize: '14px' }}>Accuracy</div>
                        </div>
                        <div 
                          onClick={actualOverPaid > 0 ? () => handleStatusFilter('Over Paid') : undefined}
                          style={{ 
                            textAlign: 'center', 
                            padding: '15px', 
                            backgroundColor: statusFilter === 'Over Paid' ? '#ffeaea' : '#f8f9fa', 
                            borderRadius: '8px',
                            cursor: actualOverPaid > 0 ? 'pointer' : 'not-allowed',
                            border: statusFilter === 'Over Paid' ? '2px solid #f44336' : '2px solid transparent',
                            transition: 'all 0.2s ease',
                            opacity: actualOverPaid === 0 ? 0.6 : 1
                          }}
                          onMouseOver={(e) => {
                            if (statusFilter !== 'Over Paid' && actualOverPaid > 0) {
                              e.target.style.backgroundColor = '#e9ecef';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (statusFilter !== 'Over Paid' && actualOverPaid > 0) {
                              e.target.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                          title={actualOverPaid > 0 ? "Click to show only overpaid coupons" : "No overpaid coupons"}
                        >
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#dc3545', marginBottom: '5px' }}>
                            {formatNumber(actualOverPaid)}
                          </div>
                          <div style={{ color: '#666', fontSize: '14px' }}>Over Paid</div>
                        </div>
                        <div 
                          onClick={actualUnderPaid > 0 ? () => handleStatusFilter('Under Paid') : undefined}
                          style={{ 
                            textAlign: 'center', 
                            padding: '15px', 
                            backgroundColor: statusFilter === 'Under Paid' ? '#fff8e1' : '#f8f9fa', 
                            borderRadius: '8px',
                            cursor: actualUnderPaid > 0 ? 'pointer' : 'not-allowed',
                            border: statusFilter === 'Under Paid' ? '2px solid #ff9800' : '2px solid transparent',
                            transition: 'all 0.2s ease',
                            opacity: actualUnderPaid === 0 ? 0.6 : 1
                          }}
                          onMouseOver={(e) => {
                            if (statusFilter !== 'Under Paid' && actualUnderPaid > 0) {
                              e.target.style.backgroundColor = '#e9ecef';
                            }
                          }}
                          onMouseOut={(e) => {
                            if (statusFilter !== 'Under Paid' && actualUnderPaid > 0) {
                              e.target.style.backgroundColor = '#f8f9fa';
                            }
                          }}
                          title={actualUnderPaid > 0 ? "Click to show only underpaid coupons" : "No underpaid coupons"}
                        >
                          <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#ffc107', marginBottom: '5px' }}>
                            {formatNumber(actualUnderPaid)}
                          </div>
                          <div style={{ color: '#666', fontSize: '14px' }}>Under Paid</div>
                        </div>
                      </div>
                    );
                  })()}
                </div>

                {/* Dynamic Status Filters */}
                {couponRedemptions.length > 0 && (
                  <div style={{
                    backgroundColor: '#ffffff',
                    borderRadius: '12px',
                    padding: '20px',
                    marginBottom: '20px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    border: '1px solid #e9ecef'
                  }}>
                    <h6 style={{ margin: '0 0 15px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                      Filter by Status
                    </h6>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                      {(() => {
                        // Calculate status counts from actual coupon data
                        const statusCounts = couponRedemptions.reduce((acc, coupon) => {
                          acc[coupon.status] = (acc[coupon.status] || 0) + 1;
                          return acc;
                        }, {});

                        const statusOrder = ['Correct', 'Over Paid', 'Under Paid', 'Discount Calc Too Low', 'Discount Calc Too High', 'Invalid'];
                        
                        return statusOrder.filter(status => statusCounts[status] > 0).map(status => (
                          <button
                            key={status}
                            onClick={() => handleStatusFilter(status)}
                            style={{
                              padding: '8px 12px',
                              borderRadius: '6px',
                              border: statusFilter === status ? '2px solid' : '2px solid transparent',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500',
                              backgroundColor: statusFilter === status 
                                ? (status === 'Correct' ? '#e8f5e8' :
                                   (status === 'Over Paid' || status === 'Under Paid') ? '#ffeaea' :
                                   (status === 'Discount Calc Too Low' || status === 'Discount Calc Too High') ? '#fff3e0' : '#e9ecef')
                                : '#f8f9fa',
                              color: statusFilter === status 
                                ? (status === 'Correct' ? '#28a745' :
                                   (status === 'Over Paid' || status === 'Under Paid') ? '#721c24' :
                                   (status === 'Discount Calc Too Low' || status === 'Discount Calc Too High') ? '#e65100' : '#6c757d')
                                : '#495057',
                              borderColor: statusFilter === status 
                                ? (status === 'Correct' ? '#28a745' :
                                   (status === 'Over Paid' || status === 'Under Paid') ? '#dc3545' :
                                   (status === 'Discount Calc Too Low' || status === 'Discount Calc Too High') ? '#fd7e14' : '#6c757d')
                                : 'transparent',
                              transition: 'all 0.2s ease'
                            }}
                            title={`Show ${statusCounts[status]} ${status.toLowerCase()} coupon${statusCounts[status] > 1 ? 's' : ''}`}
                          >
                            {status} ({statusCounts[status]})
                          </button>
                        ));
                      })()}
                      {statusFilter && (
                        <button
                          onClick={() => setStatusFilter(null)}
                          style={{
                            padding: '8px 12px',
                            borderRadius: '6px',
                            border: '2px solid #6c757d',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '500',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          Clear Filter
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Coupon Validations Table */}
                <div style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '12px',
                  padding: '25px',
                  marginBottom: '25px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  border: '1px solid #e9ecef'
                }}>
                  <h5 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '20px', fontWeight: '600' }}>
                    Coupon Validations
                    {statusFilter && (
                      <span style={{ 
                        marginLeft: '10px', 
                        fontSize: '16px', 
                        fontWeight: 'normal', 
                        color: '#666',
                        backgroundColor: '#e9ecef',
                        padding: '4px 8px',
                        borderRadius: '12px'
                      }}>
                        Filtered: {statusFilter}
                      </span>
                    )}
                  </h5>

                  {/* Quick Filters and Export */}
                  {couponRedemptions.length > 0 && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', marginBottom: '12px' }}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {/* Derived reason filters - dynamic counts */}
                        {(() => {
                          const counts = couponRedemptions.reduce((acc, c) => {
                            if (c.flags?.belowMinSpend) acc.belowMinSpend = (acc.belowMinSpend || 0) + 1;
                            if (c.flags?.exceedsMaxDiscount) acc.exceedsMaxDiscount = (acc.exceedsMaxDiscount || 0) + 1;
                            if (c.flags?.noHillsItem) acc.noHillsItem = (acc.noHillsItem || 0) + 1;
                            return acc;
                          }, {});
                          const buttons = [];
                          if (counts.belowMinSpend) {
                            buttons.push(
                              <button key="belowMinSpend" onClick={() => handleDerivedFilter('belowMinSpend')}
                                style={{ padding: '6px 10px', borderRadius: '16px', border: derivedFilter === 'belowMinSpend' ? '2px solid #007bff' : '1px solid #ced4da', backgroundColor: derivedFilter === 'belowMinSpend' ? '#e7f1ff' : '#f8f9fa', color: '#495057', fontSize: '12px', cursor: 'pointer' }}>
                                Below min spend ({counts.belowMinSpend})
                              </button>
                            );
                          }
                          if (counts.exceedsMaxDiscount) {
                            buttons.push(
                              <button key="exceedsMaxDiscount" onClick={() => handleDerivedFilter('exceedsMaxDiscount')}
                                style={{ padding: '6px 10px', borderRadius: '16px', border: derivedFilter === 'exceedsMaxDiscount' ? '2px solid #dc3545' : '1px solid #ced4da', backgroundColor: derivedFilter === 'exceedsMaxDiscount' ? '#ffeaea' : '#f8f9fa', color: '#495057', fontSize: '12px', cursor: 'pointer' }}>
                                Exceeds max discount ({counts.exceedsMaxDiscount})
                              </button>
                            );
                          }
                          if (counts.noHillsItem) {
                            buttons.push(
                              <button key="noHillsItem" onClick={() => handleDerivedFilter('noHillsItem')}
                                style={{ padding: '6px 10px', borderRadius: '16px', border: derivedFilter === 'noHillsItem' ? '2px solid #6c757d' : '1px solid #ced4da', backgroundColor: derivedFilter === 'noHillsItem' ? '#e2e3e5' : '#f8f9fa', color: '#495057', fontSize: '12px', cursor: 'pointer' }}>
                                No HILLS item ({counts.noHillsItem})
                              </button>
                            );
                          }
                          if (buttons.length === 0) return null;
                          return <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>{buttons}</div>;
                        })()}
                        {(derivedFilter || statusFilter) && (
                          <button onClick={() => { setDerivedFilter(null); setStatusFilter(null); }}
                            style={{ padding: '6px 10px', borderRadius: '16px', border: '1px solid #ced4da', backgroundColor: '#6c757d', color: '#fff', fontSize: '12px', cursor: 'pointer' }}>
                            Clear All Filters
                          </button>
                        )}
                      </div>
                      <button
                        onClick={() => {
                          // Export current filtered list
                          const rows = allFilteredCouponRedemptions.map(c => ({
                            RedeemedAt: isPlausibleDate(c.redeemed_at) ? new Date(c.redeemed_at).toISOString() : '',
                            ModeratedAt: isPlausibleDate(c.moderated_at) ? new Date(c.moderated_at).toISOString() : '',
                            Retailer: c.retailer,
                            Campaign: c.campaign_name,
                            BasketTotal: c.basket_cost,
                            Claimed: c.discount_applied,
                            'Paid out': c.detected_discount,
                            AI_Expected: c.calculated_discount,
                            Status: c.status,
                            Reasons: (c.reasons || []).join(' | '),
                            Invoice: c.invoice_uri || ''
                          }));
                          const headers = Object.keys(rows[0] || { RedeemedAt: '', ModeratedAt: '', Retailer: '', Campaign: '', BasketTotal: '', Claimed: '', 'Paid out': '', AI_Expected: '', Status: '', Reasons: '', Invoice: '' });
                          const csv = [headers.join(','), ...rows.map(r => headers.map(h => {
                            const val = r[h];
                            const str = typeof val === 'string' ? val.replace(/"/g, '""') : String(val ?? '');
                            return `"${str}"`;
                          }).join(','))].join('\n');
                          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                          const url = URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `coupon-validations-${selectedModerator?.name || 'export'}.csv`;
                          document.body.appendChild(a);
                          a.click();
                          document.body.removeChild(a);
                          URL.revokeObjectURL(url);
                        }}
                        className="drill-btn"
                        title="Export current view to CSV"
                      >
                        Export CSV
                      </button>
                    </div>
                  )}
                  
                  {couponLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                      <div className="preloader-wrapper active">
                        <div className="spinner-layer spinner-blue-only">
                          <div className="circle-clipper left">
                            <div className="circle"></div>
                          </div>
                          <div className="gap-patch">
                            <div className="circle"></div>
                          </div>
                          <div className="circle-clipper right">
                            <div className="circle"></div>
                          </div>
                        </div>
                      </div>
                      <p>Loading coupon validations...</p>
                    </div>
                  ) : couponError ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#CE060D' }}>
                      <i className="fas fa-exclamation-triangle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
                      <p>Error loading coupon validations</p>
                    </div>
                  ) : couponRedemptions.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      <i className="fas fa-info-circle" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
                      <p>No coupon validations found for this validator</p>
                    </div>
                  ) : filteredCouponRedemptions.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                      <i className="fas fa-filter" style={{ fontSize: '24px', marginBottom: '10px' }}></i>
                      <p>No {statusFilter.toLowerCase()} coupons found for this validator</p>
                      <button 
                        onClick={() => setStatusFilter(null)}
                        style={{
                          marginTop: '10px',
                          padding: '8px 16px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        Show All Coupons
                      </button>
                    </div>
                  ) : (
                    <>
                      <div style={{ overflowX: 'auto' }}>
                        <table className="striped" style={{ marginBottom: '20px' }}>
                          <thead>
                            <tr>
                              <th>Dates</th>
                              <th>Retailer</th>
                              <th>Campaign</th>
                              <th>Primary Product</th>
                              <th>Captured Total</th>
                              <th>Discount Claimed</th>
                              <th>Paid out</th>
                              <th>Claimed vs Paid out</th>
                              <th>Status</th>
                              <th>Reasons</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {filteredCouponRedemptions.map((coupon, index) => (
                              <tr key={index}>
                                <td>
                                  {isPlausibleDate(coupon.moderated_at) ? (
                                    <div title={new Date(coupon.moderated_at).toLocaleString()}>
                                      {formatRelativeDate(coupon.moderated_at)}
                                    </div>
                                  ) : (
                                    <div>-</div>
                                  )}
                                  {isPlausibleDate(coupon.redeemed_at) && (
                                    <div
                                      style={{ fontSize: '10px', color: '#6c757d' }}
                                      title={new Date(coupon.redeemed_at).toLocaleString()}
                                    >
                                      Redeemed {formatRelativeDate(coupon.redeemed_at)}
                                    </div>
                                  )}
                                </td>
                                <td style={{ fontSize: '12px', maxWidth: '160px' }}>
                                  <div title={coupon.retailer}>
                                    <span style={{ fontWeight: 600 }}>{coupon.retailer.length > 18 ? `${coupon.retailer.substring(0, 18)}...` : coupon.retailer}</span>
                                    {retailerAccuracyMap[coupon.retailer] !== undefined && (
                                      <span style={{ marginLeft: '6px', fontSize: '10px', padding: '2px 6px', borderRadius: '10px', backgroundColor: '#e9f7ef', color: '#155724', border: '1px solid #c3e6cb' }}
                                        title={`Store accuracy`}>
                                        {formatPercentage(retailerAccuracyMap[coupon.retailer])}
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td style={{ fontSize: '12px', maxWidth: '150px' }}>
                                  <div title={coupon.campaign_name}>
                                    {coupon.campaign_name.length > 25 ? 
                                      `${coupon.campaign_name.substring(0, 25)}...` : 
                                      coupon.campaign_name}
                                  </div>
                                </td>
                                <td style={{ fontSize: '12px', maxWidth: '200px' }}>
                                  <div 
                                    title={`${coupon.primaryProduct.name} - Click to see full basket`}
                                    onClick={() => showBasketDetails(coupon)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <strong>{coupon.primaryProduct.brand}</strong><br/>
                                    {coupon.primaryProduct.name.length > 30 ? 
                                      `${coupon.primaryProduct.name.substring(0, 30)}...` : 
                                      coupon.primaryProduct.name}
                                  </div>
                                  {coupon.basket_size > 1 && (
                                    <small 
                                      style={{ color: '#007bff', cursor: 'pointer', textDecoration: 'underline' }}
                                      onClick={() => showBasketDetails(coupon)}
                                    >
                                      +{coupon.basket_size - 1} more item{coupon.basket_size > 2 ? 's' : ''} (click to view)
                                    </small>
                                  )}
                                </td>
                                <td>{formatCurrency(coupon.basket_cost)}</td>
                                <td>
                                  {formatCurrency(coupon.discount_applied)}
                                  {typeof coupon.calculated_discount === 'number' && (
                                    <div style={{ fontSize: '10px', color: '#6c757d' }} title={`AI expected ${formatCurrency(coupon.calculated_discount)}`}>
                                      AI: {formatCurrency(coupon.calculated_discount)}
                                    </div>
                                  )}
                                </td>
                                <td>{formatCurrency(coupon.detected_discount)}</td>
                                <td style={{ 
                                  fontWeight: 'bold'
                                }}>
                                  {(() => {
                                    const discountClaimed = coupon.discount_applied || 0;
                                    const actuallyGiven = coupon.detected_discount || 0;
                                    // Customer perspective: what they got vs what was claimed
                                    const difference = actuallyGiven - discountClaimed;
                                    
                                    if (Math.abs(difference) <= 5.00) {
                                      return <span style={{ color: '#28a745' }}>—</span>; // Within tolerance - green
                                    }
                                    
                                    const color = difference < 0 ? '#dc3545' : '#fd7e14'; // Red for negative (underpaid), orange for positive (overpaid)
                                    const displayValue = difference < 0 
                                      ? `-R${Math.abs(difference).toFixed(2)}`
                                      : `+R${difference.toFixed(2)}`;
                                    
                                    return <span style={{ color }}>{displayValue}</span>;
                                  })()}
                                </td>
                                <td>
                                  {coupon.status === 'Correct' ? (
                                    <i className="fas fa-check-circle" style={{
                                      color: '#28a745',
                                      fontSize: '18px'
                                    }}></i>
                                  ) : (
                                    <span style={{
                                      padding: '4px 8px',
                                      borderRadius: '12px',
                                      fontSize: '12px',
                                      fontWeight: 'bold',
                                      backgroundColor: 
                                        (coupon.status === 'Over Paid' || coupon.status === 'Under Paid') ? '#f8d7da' :
                                        (coupon.status === 'Discount Calc Too Low' || coupon.status === 'Discount Calc Too High') ? '#fff3e0' : '#e2e3e5',
                                      color:
                                        (coupon.status === 'Over Paid' || coupon.status === 'Under Paid') ? '#721c24' :
                                        (coupon.status === 'Discount Calc Too Low' || coupon.status === 'Discount Calc Too High') ? '#e65100' : '#6c757d'
                                    }}>
                                      {coupon.status}
                                    </span>
                                  )}
                                  {coupon.is_test && (
                                    <span style={{
                                      marginLeft: '5px',
                                      padding: '2px 6px',
                                      borderRadius: '8px',
                                      fontSize: '10px',
                                      backgroundColor: '#e2e3e5',
                                      color: '#6c757d'
                                    }}>
                                      TEST
                                    </span>
                                  )}
                                </td>
                                <td style={{ maxWidth: '240px' }}>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                    {(coupon.reasons || []).slice(0, 4).map((r, i) => (
                                      <span key={i} style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '12px', backgroundColor: '#f1f3f5', color: '#495057', border: '1px solid #dee2e6' }}>{r}</span>
                                    ))}
                                    {(coupon.reasons || []).length > 4 && (
                                      <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '12px', backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffeaa7' }}>+{(coupon.reasons || []).length - 4} more</span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                    <button
                                      type="button"
                                      onClick={() => showInvoicePreview(coupon)}
                                      className="drill-btn"
                                    >
                                      <i className="fas fa-file-invoice" style={{ marginRight: '6px' }} aria-hidden="true" />
                                      View Invoice
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleRevalidateCouponMetadata(coupon)}
                                      disabled={!coupon.coupon_metadata_id || revalidatingCouponId === coupon.coupon_metadata_id}
                                      className="drill-btn drill-btn--icon-secondary"
                                      title="Re-fetch and refresh coupon metadata from the server"
                                      aria-label={revalidatingCouponId === coupon.coupon_metadata_id ? 'Refreshing metadata' : 'Refresh coupon metadata'}
                                    >
                                      {revalidatingCouponId === coupon.coupon_metadata_id ? (
                                        <i className="fas fa-spinner fa-spin" aria-hidden="true" />
                                      ) : (
                                        <i className="fas fa-sync-alt" aria-hidden="true" />
                                      )}
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      
                      {/* Coupon Pagination Controls */}
                      {effectivePagination.total > 0 && (
                        <div style={{ marginTop: '20px', borderTop: '1px solid #e9ecef', paddingTop: '20px' }}>
                          {/* Pagination Info */}
                          <div style={{ textAlign: 'center', color: '#666', marginBottom: '15px' }}>
                            <p>
                  Showing {filteredCouponRedemptions.length} 
                  {statusFilter ? ` ${statusFilter.toLowerCase()}` : ''} 
                  {' '}of {formatNumber(effectivePagination.total)} coupon validations
                  {statusFilter && (
                    <button 
                      onClick={() => setStatusFilter(null)}
                      style={{
                        marginLeft: '10px',
                        padding: '4px 8px',
                        backgroundColor: '#6c757d',
                        color: 'white',
                        border: 'none',
                        borderRadius: '3px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Clear Filter
                    </button>
                  )}
                </p>
                            {effectivePagination.pageCount > 1 && (
                              <p>Page {couponCurrentPage} of {effectivePagination.pageCount}</p>
                            )}
                          </div>
                          
                          {/* Pagination Controls */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                            {/* Page Size Selector */}
                            <div style={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '15px',
                              backgroundColor: '#f8f9fa',
                              padding: '12px 16px',
                              borderRadius: '8px',
                              border: '2px solid #e9ecef',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                            }}>
                              <label style={{ 
                                fontSize: '15px', 
                                color: '#495057',
                                fontWeight: '600',
                                whiteSpace: 'nowrap'
                              }}>
                                Items per page:
                              </label>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                {[10, 25, 50, 100].map(size => (
                                  <button
                                    key={size}
                                    onClick={() => handleCouponPageSizeChange(size)}
                                    style={{
                                      padding: '8px 16px',
                                      border: couponPageSize === size ? '2px solid #007bff' : '2px solid #ced4da',
                                      borderRadius: '6px',
                                      backgroundColor: couponPageSize === size ? '#007bff' : '#ffffff',
                                      color: couponPageSize === size ? '#ffffff' : '#495057',
                                      cursor: 'pointer',
                                      fontSize: '14px',
                                      fontWeight: '500',
                                      transition: 'all 0.2s ease',
                                      minWidth: '50px'
                                    }}
                                  >
                                    {size}
                                  </button>
                                ))}
                              </div>
                            </div>
                            
                            {/* Page Navigation */}
                            {effectivePagination.pageCount > 1 && (
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <button 
                                  onClick={() => handleCouponPageChange(couponCurrentPage - 1)}
                                  disabled={couponCurrentPage === 1}
                                  style={{ 
                                    padding: '8px 12px', 
                                    border: '1px solid #ddd', 
                                    borderRadius: '4px',
                                    backgroundColor: couponCurrentPage === 1 ? '#f5f5f5' : '#fff',
                                    color: couponCurrentPage === 1 ? '#999' : '#333',
                                    cursor: couponCurrentPage === 1 ? 'not-allowed' : 'pointer',
                                    fontSize: '14px'
                                  }}
                                >
                                  Previous
                                </button>
                                
                                {/* Page Numbers */}
                                <div style={{ display: 'flex', gap: '5px' }}>
                                  {/* First page */}
                                  {couponCurrentPage > 3 && (
                                    <>
                                      <button 
                                        onClick={() => handleCouponPageChange(1)}
                                        style={{ 
                                          padding: '8px 12px', 
                                          border: '1px solid #ddd', 
                                          borderRadius: '4px',
                                          backgroundColor: '#fff',
                                          color: '#333',
                                          cursor: 'pointer',
                                          fontSize: '14px'
                                        }}
                                      >
                                        1
                                      </button>
                                      {couponCurrentPage > 4 && <span style={{ padding: '8px 4px', color: '#999' }}>...</span>}
                                    </>
                                  )}
                                  
                                  {/* Pages around current */}
                                  {[...Array(effectivePagination.pageCount)].map((_, index) => {
                                    const pageNum = index + 1;
                                    if (pageNum >= couponCurrentPage - 2 && pageNum <= couponCurrentPage + 2) {
                                      return (
                                        <button 
                                          key={pageNum}
                                          onClick={() => handleCouponPageChange(pageNum)}
                                          style={{ 
                                            padding: '8px 12px', 
                                            border: '1px solid #ddd', 
                                            borderRadius: '4px',
                                            backgroundColor: pageNum === couponCurrentPage ? '#007bff' : '#fff',
                                            color: pageNum === couponCurrentPage ? '#fff' : '#333',
                                            cursor: 'pointer',
                                            fontSize: '14px',
                                            fontWeight: pageNum === couponCurrentPage ? 'bold' : 'normal'
                                          }}
                                        >
                                          {pageNum}
                                        </button>
                                      );
                                    }
                                    return null;
                                  })}
                                  
                                  {/* Last page */}
                                  {couponCurrentPage < effectivePagination.pageCount - 2 && (
                                    <>
                                      {couponCurrentPage < effectivePagination.pageCount - 3 && <span style={{ padding: '8px 4px', color: '#999' }}>...</span>}
                                      <button 
                                        onClick={() => handleCouponPageChange(effectivePagination.pageCount)}
                                        style={{ 
                                          padding: '8px 12px', 
                                          border: '1px solid #ddd', 
                                          borderRadius: '4px',
                                          backgroundColor: '#fff',
                                          color: '#333',
                                          cursor: 'pointer',
                                          fontSize: '14px'
                                        }}
                                      >
                                        {effectivePagination.pageCount}
                                      </button>
                                    </>
                                  )}
                                </div>
                                
                                <button 
                                  onClick={() => handleCouponPageChange(couponCurrentPage + 1)}
                                  disabled={couponCurrentPage === effectivePagination.pageCount}
                                  style={{ 
                                    padding: '8px 12px', 
                                    border: '1px solid #ddd', 
                                    borderRadius: '4px',
                                    backgroundColor: couponCurrentPage === effectivePagination.pageCount ? '#f5f5f5' : '#fff',
                                    color: couponCurrentPage === effectivePagination.pageCount ? '#999' : '#333',
                                    cursor: couponCurrentPage === effectivePagination.pageCount ? 'not-allowed' : 'pointer',
                                    fontSize: '14px'
                                  }}
                                >
                                  Next
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                  </div>
                )}

                {/* Daily Trends Tab Content - hidden */}

                {/* Error Pattern Analysis Tab Content */}
                {activeTab === 'error-patterns' && (
                  <div>
                    {analyticsLoading ? (
                      <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                        <i className="fas fa-spinner fa-spin" style={{ fontSize: '24px', marginBottom: '15px' }}></i>
                        <p>Loading error analysis...</p>
                      </div>
                    ) : (
                      <div>
                        <h5 style={{ margin: '0 0 25px 0', color: '#333', fontSize: '20px', fontWeight: '600' }}>
                          Error Pattern Analysis
                        </h5>
                        
                        {/* Error Types Overview */}
                        <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
                          <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                            <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px', color: '#dc3545' }}></i>
                            Error Types Distribution
                          </h6>
                          {analyticsData.errorPatterns.length > 0 ? (
                            <div className="chart-container">
                              <Chart
                                options={{
                                  chart: { type: 'pie', height: 400, background: '#FFFFFF' },
                                  labels: analyticsData.errorPatterns?.map(e => e.type || 'Unknown') || ['No Data'],
                                  colors: ['#dc3545', '#fd7e14', '#ffc107', '#6f42c1', '#e83e8c'],
                                  title: { text: 'Error Types by Frequency' },
                                  legend: { position: 'bottom' }
                                }}
                                series={analyticsData.errorPatterns?.map(e => e.count || 0) || [1]}
                                type="pie"
                                height={400}
                              />
                            </div>
                          ) : (
                            <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No error data available</p>
                          )}
                        </div>

                        {/* Error Severity Analysis */}
                        <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
                          <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                            <i className="fas fa-chart-bar" style={{ marginRight: '8px', color: '#fd7e14' }}></i>
                            Error Severity by Amount
                          </h6>
                          {analyticsData.errorPatterns.length > 0 ? (
                            <div className="chart-container">
                              <Chart
                                options={{
                                  chart: { type: 'bar', height: 300, background: '#FFFFFF' },
                                  xaxis: { 
                                    categories: analyticsData.errorPatterns?.map(e => e.type || 'Unknown') || ['No Data'],
                                    title: { text: 'Error Type' }
                                  },
                                  yaxis: { title: { text: 'Average Amount (R)' } },
                                  colors: ['#fd7e14'],
                                  title: { text: 'Average Error Amount by Type' }
                                }}
                                series={[{
                                  name: 'Avg Error Amount (R)',
                                  data: analyticsData.errorPatterns?.map(e => parseFloat((e.avgAmount || 0).toFixed(2))) || [0]
                                }]}
                                type="bar"
                                height={300}
                              />
                            </div>
                          ) : (
                            <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No error data available</p>
                          )}
                        </div>

                        {/* Common Reasons */}
                        <div style={{ backgroundColor: '#ffffff', borderRadius: '12px', padding: '25px', marginBottom: '25px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', border: '1px solid #e9ecef' }}>
                          <h6 style={{ margin: '0 0 20px 0', color: '#333', fontSize: '16px', fontWeight: '600' }}>
                            <i className="fas fa-list" style={{ marginRight: '8px', color: '#6c757d' }}></i>
                            Most Common Error Reasons
                          </h6>
                          {analyticsData.errorPatterns.length > 0 ? (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
                              {analyticsData.errorPatterns.slice(0, 3).map((errorType, index) => (
                                <div key={index} style={{ border: '1px solid #e9ecef', borderRadius: '8px', padding: '15px' }}>
                                  <h6 style={{ margin: '0 0 10px 0', color: '#333', fontSize: '14px', fontWeight: '600' }}>
                                    {errorType.type}
                                  </h6>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {Object.entries(errorType.commonReasons || {})
                                      .sort(([,a], [,b]) => b - a)
                                      .slice(0, 5)
                                      .map(([reason, count]) => (
                                        <div key={reason} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                                          <span>{reason}</span>
                                          <span style={{ fontWeight: '600' }}>{count}</span>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p style={{ color: '#666', textAlign: 'center', padding: '20px' }}>No error reasons available</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Share Invite Tab */}
                {activeTab === 'share-invite' && (
                  <div>
                    <div className="section-title">Share Validator Drilldown</div>
                    <div style={{ 
                      backgroundColor: '#ffffff', 
                      borderRadius: '12px', 
                      padding: '25px', 
                      marginBottom: '25px', 
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)', 
                      border: '1px solid #e9ecef' 
                    }}>
                      <ShareInviteForm 
                        moderator={selectedModerator}
                        deepLink={shareInviteLink}
                      />
                    </div>
                  </div>
                )}

                {/* Other Analytics Tabs */}
                {activeTab !== 'overview' && activeTab !== 'daily-trends' && activeTab !== 'error-patterns' && (
                  <ValidatorAnalyticsTabs
                    activeTab={activeTab}
                    analyticsData={analyticsData}
                    analyticsLoading={analyticsLoading}
                    selectedModerator={selectedModerator}
                    allModerators={allModerators}
                  />
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '60px 20px', color: '#666' }}>
                <p>No validator data available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Similar Slips Modal */}
      {similarOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
          <div style={{ backgroundColor: '#fff', borderRadius: '12px', width: '900px', maxWidth: '95vw', maxHeight: '80vh', overflow: 'auto', border: '1px solid #e9ecef', boxShadow: '0 10px 25px rgba(0,0,0,0.3)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid #e9ecef' }}>
              <h5 style={{ margin: 0 }}>
                Similar Slips
                {similarContext.retailer && (
                  <span style={{ marginLeft: '8px', color: '#6c757d', fontSize: '14px' }}>
                    {similarContext.retailer} · {similarContext.campaign}
                  </span>
                )}
              </h5>
              <button onClick={() => setSimilarOpen(false)} style={{ background: 'none', border: 'none', fontSize: '24px', cursor: 'pointer', color: '#666' }}>×</button>
            </div>
            <div style={{ padding: '16px 20px' }}>
              {similarItems.length === 0 ? (
                <div style={{ textAlign: 'center', color: '#666', padding: '30px' }}>
                  No similar slips found in current dataset
                </div>
              ) : (
                <table className="striped">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Claimed</th>
                      <th>Paid out</th>
                      <th>AI Expected</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {similarItems.slice(0, 50).map((c, idx) => (
                      <tr key={idx}>
                        <td
                          title={isPlausibleDate(c.redeemed_at) ? `Redeemed ${new Date(c.redeemed_at).toLocaleString()}` : undefined}
                        >
                          {isPlausibleDate(c.moderated_at) ? formatRelativeDate(c.moderated_at) : '-'}
                        </td>
                        <td>{formatCurrency(c.discount_applied)}</td>
                        <td>{formatCurrency(c.detected_discount)}</td>
                        <td>{typeof c.calculated_discount === 'number' ? formatCurrency(c.calculated_discount) : '-'}</td>
                        <td>{c.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Basket Details Modal */}
      {basketDetailsOpen && selectedBasket && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          zIndex: 10001,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '25px',
            width: '900px',
            maxWidth: '90vw',
            maxHeight: '80vh',
            overflow: 'auto',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
            border: '1px solid #e9ecef'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              borderBottom: '2px solid #e9ecef',
              paddingBottom: '15px'
            }}>
              <div>
                <h5 style={{ margin: 0, color: '#333', fontSize: '18px', fontWeight: '600' }}>
                  Basket Details
                </h5>
                <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                  Coupon: {selectedBasket.actual_code} | {selectedBasket.campaign_name}
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={() => showInvoicePreview(selectedBasket)}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #28a745',
                    borderRadius: '6px',
                    backgroundColor: '#28a745',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#1e7e34';
                    e.target.style.borderColor = '#1e7e34';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#28a745';
                    e.target.style.borderColor = '#28a745';
                  }}
                  title="View invoice"
                >
                  <i className="fas fa-receipt" style={{ marginRight: '5px' }}></i>
                  Invoice
                </button>
                <button
                  onClick={closeBasketDetails}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '24px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '5px',
                    borderRadius: '50%',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.color = '#333';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#666';
                  }}
                  title="Close"
                >
                  ×
                </button>
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Product</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Brand</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Species</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Life Stage</th>
                      <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Category</th>
                      <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6', fontSize: '14px' }}>Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selectedBasket.products.map((product, index) => (
                      <tr key={index} style={{ borderBottom: '1px solid #e9ecef' }}>
                        <td style={{ padding: '10px', fontSize: '12px', maxWidth: '250px' }}>
                          <div title={product.name}>
                            {product.name.length > 40 ? `${product.name.substring(0, 40)}...` : product.name}
                          </div>
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px', fontWeight: 'bold' }}>
                          <span style={{
                            padding: '2px 6px',
                            borderRadius: '8px',
                            fontSize: '10px',
                            backgroundColor: product.brand === 'HILLS' ? '#d4edda' : '#e2e3e5',
                            color: product.brand === 'HILLS' ? '#155724' : '#6c757d'
                          }}>
                            {product.brand}
                          </span>
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px' }}>
                          {product.species ? (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '6px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              backgroundColor: product.species === 'DOG' ? '#e3f2fd' : product.species === 'CAT' ? '#fce4ec' : '#f3e5f5',
                              color: product.species === 'DOG' ? '#1565c0' : product.species === 'CAT' ? '#c2185b' : '#7b1fa2'
                            }}>
                              {product.species}
                            </span>
                          ) : (
                            <span style={{ color: '#999', fontSize: '11px' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px' }}>
                          {product.life_stage ? (
                            <span style={{
                              padding: '2px 6px',
                              borderRadius: '6px',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              backgroundColor: '#fff3e0',
                              color: '#e65100'
                            }}>
                              {product.life_stage}
                            </span>
                          ) : (
                            <span style={{ color: '#999', fontSize: '11px' }}>—</span>
                          )}
                        </td>
                        <td style={{ padding: '10px', fontSize: '12px' }}>{product.category}</td>
                        <td style={{ padding: '10px', fontSize: '12px', textAlign: 'right', fontWeight: 'bold' }}>
                          R{product.cost.toFixed(2)}
                        </td>
                      </tr>
                    ))}
                    <tr style={{ backgroundColor: '#f8f9fa', fontWeight: 'bold' }}>
                      <td colSpan="5" style={{ padding: '10px', fontSize: '14px', textAlign: 'right' }}>
                        Total Basket Cost:
                      </td>
                      <td style={{ padding: '10px', fontSize: '14px', textAlign: 'right' }}>
                        R{selectedBasket.basket_cost.toFixed(2)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
              gap: '15px',
              padding: '15px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#007bff' }}>
                  {selectedBasket.basket_size}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Items</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#28a745' }}>
                  {selectedBasket.hillsProductCount}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Hills Products</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#28a745' }}>
                  R{selectedBasket.products.filter(p => p.brand === 'HILLS').reduce((sum, p) => sum + p.cost, 0).toFixed(2)}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Hills Total</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#17a2b8' }}>
                  R{selectedBasket.discount_applied.toFixed(2)}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Discount Applied</div>
              </div>
              <div style={{ textAlign: 'center' }}>
                                        <div style={{ fontSize: '16px', fontWeight: 'bold', color: (!selectedBasket.hasCalculationError && !selectedBasket.hasDetectionError) ? '#666' : (selectedBasket.calculationDifference > 0.01 || selectedBasket.detectionDifference > 0.01) ? '#dc3545' : '#28a745' }}>
                          {(!selectedBasket.hasCalculationError && !selectedBasket.hasDetectionError) ? '—' : 
                           [
                             selectedBasket.hasCalculationError && {
                               icon: <i className="fas fa-file-alt" style={{ marginRight: '2px', color: '#555555', fontSize: '12px' }}></i>,
                               amount: selectedBasket.calculationDifference > 0 ? `+R${selectedBasket.calculationDifference.toFixed(2)}` : `-R${Math.abs(selectedBasket.calculationDifference).toFixed(2)}`
                             },
                             selectedBasket.hasDetectionError && {
                               icon: <i className="fas fa-user" style={{ marginRight: '2px', color: '#555555', fontSize: '12px' }}></i>,
                               amount: selectedBasket.detectionDifference > 0 ? `+R${selectedBasket.detectionDifference.toFixed(2)}` : `-R${Math.abs(selectedBasket.detectionDifference).toFixed(2)}`
                             }
                           ].filter(Boolean).map((item, index) => (
                             <span key={index}>{item.icon}{item.amount}</span>
                           )).reduce((prev, curr, index) => 
                             index === 0 ? [curr] : [...prev, ' | ', curr], []
                           )}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>Difference</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Preview Modal */}
      {invoicePreviewOpen && selectedInvoice && (
        <div 
          onClick={closeInvoicePreview}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.8)',
            zIndex: 10002,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px'
          }}>
          <div 
            onClick={(e) => e.stopPropagation()}
            style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '0',
            width: '1000px',
            maxWidth: '95vw',
            maxHeight: '90vh',
            overflow: 'hidden',
            boxShadow: '0 10px 25px rgba(0,0,0,0.3)',
            border: '1px solid #e9ecef',
            display: 'flex',
            flexDirection: 'column'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '20px 25px',
              height: '80px',
              borderBottom: '2px solid #555555',
              backgroundColor: '#f8f9fa'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <button
                  onClick={closeInvoicePreview}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: '#666',
                    padding: '8px',
                    borderRadius: '50%',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '40px',
                    height: '40px'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#f8f9fa';
                    e.target.style.color = '#333';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#666';
                  }}
                  title="Back"
                >
                  <i className="fas fa-chevron-left"></i>
                </button>
                <div>
                  <h5 style={{ margin: 0, color: '#333', fontSize: '18px', fontWeight: '600' }}>
                    {selectedInvoice.campaign_name}
                  </h5>
                  <p style={{ margin: '5px 0 0 0', color: '#666', fontSize: '14px' }}>
                    Coupon: {selectedInvoice.actual_code}
                  </p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  onClick={() => window.open(selectedInvoice.invoice_uri, '_blank')}
                  style={{
                    padding: '8px 16px',
                    border: '1px solid #007bff',
                    borderRadius: '6px',
                    backgroundColor: '#007bff',
                    color: '#ffffff',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.target.style.backgroundColor = '#0056b3';
                    e.target.style.borderColor = '#0056b3';
                  }}
                  onMouseOut={(e) => {
                    e.target.style.backgroundColor = '#007bff';
                    e.target.style.borderColor = '#007bff';
                  }}
                  title="Open in new tab"
                >
                  <i className="fas fa-external-link-alt" style={{ marginRight: '5px' }}></i>
                  Open
                </button>
              </div>
            </div>

            {/* Content */}
            <div style={{
              position: 'relative',
              height: 'calc(90vh - 160px)', // Account for header height (80px) and footer + padding
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              backgroundColor: '#f8f9fa',
              overflow: 'auto'
            }}>
              {(() => {
                const invoiceType = getInvoiceType(selectedInvoice.invoice_uri);
                
                if (invoiceType === 'pdf') {
                  return (
                    <iframe
                      src={`${selectedInvoice.invoice_uri}#toolbar=0`}
                      style={{
                        width: '100%',
                        height: '70vh',
                        border: 'none',
                        backgroundColor: '#ffffff'
                      }}
                      title="Invoice PDF"
                    />
                  );
                } else if (invoiceType === 'image') {
                  return (
                    <div style={{ position: 'relative', width: '100%', height: '70vh', display: 'flex', flexDirection: 'column' }}>
                      {/* Image Controls */}
                      <div style={{
                        position: 'absolute',
                        top: '10px',
                        right: '10px',
                        zIndex: 1000,
                        display: 'flex',
                        gap: '5px',
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        borderRadius: '8px',
                        padding: '8px'
                      }}>
                        <button
                          onClick={handleImageRotate}
                          style={{
                            background: 'rgba(255,255,255,0.9)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            height: '32px'
                          }}
                          title="Rotate 90°"
                        >
                          <i className="fas fa-undo"></i>
                        </button>
                        <button
                          onClick={handleImageZoomOut}
                          style={{
                            background: 'rgba(255,255,255,0.9)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            height: '32px'
                          }}
                          title="Zoom Out"
                        >
                          <i className="fas fa-search-minus"></i>
                        </button>
                        <span style={{
                          background: 'rgba(255,255,255,0.9)',
                          borderRadius: '4px',
                          padding: '8px',
                          fontSize: '12px',
                          color: '#333',
                          display: 'flex',
                          alignItems: 'center',
                          minWidth: '50px',
                          justifyContent: 'center'
                        }}>
                          {Math.round(imageZoom * 100)}%
                        </span>
                        <button
                          onClick={handleImageZoomIn}
                          style={{
                            background: 'rgba(255,255,255,0.9)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            height: '32px'
                          }}
                          title="Zoom In"
                        >
                          <i className="fas fa-search-plus"></i>
                        </button>
                        <button
                          onClick={handleImageFullscreen}
                          style={{
                            background: 'rgba(255,255,255,0.9)',
                            border: 'none',
                            borderRadius: '4px',
                            padding: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            color: '#333',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            minWidth: '32px',
                            height: '32px'
                          }}
                          title="Open Fullscreen"
                        >
                          <i className="fas fa-expand"></i>
                        </button>
                      </div>
                      
                      {/* Image Container */}
                      <div 
                        style={{ 
                          flex: 1, 
                          display: 'flex', 
                          justifyContent: 'center', 
                          alignItems: 'center',
                          overflow: imageZoom > 1 ? 'hidden' : 'auto',
                          backgroundColor: '#f8f9fa',
                          cursor: imageZoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                          position: 'relative'
                        }}
                        onMouseMove={handleImageMouseMove}
                        onMouseUp={handleImageMouseUp}
                        onMouseLeave={handleImageMouseLeave}
                      >
                        <img
                          src={selectedInvoice.invoice_uri}
                          alt="Invoice"
                          draggable={false}
                          style={{
                            maxWidth: imageZoom > 1 ? 'none' : '100%',
                            maxHeight: imageZoom > 1 ? 'none' : '100%',
                            width: imageZoom > 1 ? `${imageZoom * 100}%` : 'auto',
                            height: imageZoom > 1 ? `${imageZoom * 100}%` : 'auto',
                            objectFit: 'contain',
                            backgroundColor: '#ffffff',
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                            transform: `rotate(${imageRotation}deg) translate(${imageZoom > 1 ? imagePanX : 0}px, ${imageZoom > 1 ? imagePanY : 0}px)`,
                            transition: isDragging ? 'none' : 'transform 0.1s ease-out',
                            userSelect: 'none'
                          }}
                          onMouseDown={handleImageMouseDown}
                          onError={(e) => {
                            // Hide the image and show error message
                            e.target.style.display = 'none';
                            const errorDiv = e.target.parentElement.parentElement.querySelector('.image-error-fallback');
                            if (errorDiv) {
                              errorDiv.style.display = 'block';
                            }
                          }}
                        />
                      </div>
                      
                      <div 
                        className="image-error-fallback"
                        style={{
                          display: 'none',
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          textAlign: 'center',
                          padding: '60px 40px',
                          color: '#666',
                          backgroundColor: '#fff',
                          borderRadius: '8px',
                          boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                        }}
                      >
                        <i className="fas fa-exclamation-triangle" style={{ fontSize: '48px', marginBottom: '20px', color: '#ffc107' }}></i>
                        <h6 style={{ margin: '0 0 15px 0', color: '#333' }}>
                          Failed to Load Invoice Image
                        </h6>
                        <p style={{ margin: '0 0 20px 0', fontSize: '14px' }}>
                          There was an error loading the invoice image.
                        </p>
                        <button
                          onClick={() => window.open(selectedInvoice.invoice_uri, '_blank')}
                          style={{
                            padding: '10px 20px',
                            border: '1px solid #007bff',
                            borderRadius: '6px',
                            backgroundColor: '#007bff',
                            color: '#ffffff',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '500'
                          }}
                        >
                          <i className="fas fa-external-link-alt" style={{ marginRight: '8px' }}></i>
                          Open in New Tab
                        </button>
                      </div>
                    </div>
                  );
                } else {
                  // Fallback for unknown types
                  return (
                    <div style={{
                      textAlign: 'center',
                      padding: '60px 40px',
                      color: '#666'
                    }}>
                      <i className="fas fa-file-alt" style={{ fontSize: '48px', marginBottom: '20px', color: '#007bff' }}></i>
                      <h6 style={{ margin: '0 0 15px 0', color: '#333' }}>
                        Invoice Preview Not Available
                      </h6>
                      <p style={{ margin: '0 0 20px 0', fontSize: '14px' }}>
                        The invoice format cannot be previewed in browser.
                      </p>
                      <button
                        onClick={() => window.open(selectedInvoice.invoice_uri, '_blank')}
                        style={{
                          padding: '10px 20px',
                          border: '1px solid #007bff',
                          borderRadius: '6px',
                          backgroundColor: '#007bff',
                          color: '#ffffff',
                          cursor: 'pointer',
                          fontSize: '14px',
                          fontWeight: '500'
                        }}
                      >
                        <i className="fas fa-download" style={{ marginRight: '8px' }}></i>
                        Download Invoice
                      </button>
                    </div>
                  );
                }
                            })()}
            </div>

            {/* Footer with invoice details */}
            <div style={{
              padding: '11px 25px',
              borderTop: '1px solid #e9ecef',
              backgroundColor: '#f8f9fa',
              height: '120px'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '25px',
                padding: '14px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px'
              }}>
                {/* CAPTURED BY VET Section */}
                <div>
                  <h6 style={{ 
                    margin: '0 0 6px 0', 
                    fontSize: '14px', 
                    fontWeight: 'bold', 
                    color: '#333',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    CAPTURED BY VET:
                  </h6>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#666' }}>Total Spend:</span>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#007bff' }}>
                        R{selectedInvoice.basket_cost.toFixed(2)}
                      </span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#666' }}>Discount claimed:</span>
                      <span style={{ fontSize: '16px', fontWeight: 'bold', color: '#007bff' }}>
                        R{selectedInvoice.discount_applied.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* DISCOUNT Section */}
                <div>
                  <h6 style={{ 
                    margin: '0 0 6px 0', 
                    fontSize: '14px', 
                    fontWeight: 'bold', 
                    color: '#333',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    PROCESSED BY AI:
                  </h6>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#666', width: '140px' }}>Estimated Due (ai):</span>
                      <span style={{ 
                        fontSize: '16px', 
                        fontWeight: 'bold', 
                        color: Math.abs(selectedInvoice.discount_applied - selectedInvoice.calculated_discount) < 0.01 ? '#28a745' : '#ff8c00',
                        paddingLeft: '80px'
                      }}>
                        <i className="fas fa-file-alt" style={{ marginRight: '4px', color: '#555555' }}></i>
                        R{selectedInvoice.calculated_discount.toFixed(2)}
                        {Math.abs(selectedInvoice.discount_applied - selectedInvoice.calculated_discount) >= 0.01 && 
                          <span>
                            {' '}
                            ({selectedInvoice.calculated_discount - selectedInvoice.discount_applied >= 0 ? '+' : ''}{(selectedInvoice.calculated_discount - selectedInvoice.discount_applied).toFixed(2)})
                          </span>
                        }
                      </span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <span style={{ fontSize: '14px', color: '#666', width: '140px' }}>Actually Given:</span>
                      <span>
                        <span style={{ 
                          fontSize: '16px', 
                          fontWeight: 'bold', 
                          color: '#007bff',
                          paddingLeft: '80px'
                        }}>
                          <i className="fas fa-user" style={{ marginRight: '4px', color: '#555555' }}></i>
                          R{selectedInvoice.detected_discount.toFixed(2)}
                        </span>
                        {Math.abs(selectedInvoice.discount_applied - selectedInvoice.detected_discount) >= 0.01 && 
                          <span style={{ 
                            fontSize: '16px', 
                            fontWeight: 'bold', 
                            color: '#ff8c00'
                          }}>
                            {' '}
                            ({selectedInvoice.detected_discount - selectedInvoice.discount_applied >= 0 ? '+' : ''}{(selectedInvoice.detected_discount - selectedInvoice.discount_applied).toFixed(2)})
                          </span>
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModeratorPerformance; 